import React from 'react'
import { CircularProgress } from '@mui/material'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { A4DpiToDetailsMap } from 'src/consumer/constants'
import { mediaURL } from 'src/consumer/services'
import { isPWA } from 'src/services/mediaPermissions/permissionConstants'
import { downloadPDFFromURL, generatePrescriptionPDF } from '../prescriptionHelperFunctions'
import { sleep } from 'src/mentor/helpers/utilityFunctions'

/**
 * Returns print action functions for the PrescriptionMakingPage.
 * All functions close over the `deps` object passed in.
 *
 * @param {object} deps - State, setters, refs and helpers from PrescriptionMakingPage
 */
export default function usePrintActions(deps) {
  const {
    pdfGenerating,
    setPdfGenerating,
    loadingToastIdRef,
    generateClientSidePDFForPrint,
    savePrescriptionData,
    colorMode,
    injectColorModeCSS,
    totalPages,
    setIsDownloadMode,
    setPdfDrawerOpen,
    setPdfProgressMessage,
    setShouldAutoPrint,
    setPdfBlob,
    setPdfUrl,
    setQuickPrintDrawerOpen,
    setClonedPagesHTML,
    pdfBlob,
    prescriptionData,
    printSettings,
    bookedEventInfo,
    drugsAdvice,
  } = deps

  async function saveAndPrint(selectedPages = null) {
    // Prevent duplicate calls - if PDF is already being generated, skip
    if (pdfGenerating) {
      console.warn('[Save & Print] PDF generation already in progress, skipping duplicate call')
      return
    }
    
    // Set generating flag immediately to prevent race conditions
    setPdfGenerating(true)
    
    // Use PDF generation approach for printing to ensure all pages are included
    // Generate PDF and open in new window for printing
    // Pass autoPrint: true to automatically trigger print dialog
    // PDF generation will handle saving in background
    
    // Show persistent loading toast
    const loadingToastId = toast.loading(
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <CircularProgress size={20} sx={{ color: 'white' }} />
        <span>Signing & Issuing Prescription...</span>
      </div>,
      { 
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        draggable: false,
        position: 'top-center',
        style: {
          minWidth: '300px',
          fontSize: '16px',
          fontWeight: 500,
        }
      }
    )
    
    // Set ref immediately so it's accessible everywhere
    loadingToastIdRef.current = loadingToastId
    console.log('[Save & Print] Loading toast created, ID:', loadingToastId)
    
    try {
      await generateClientSidePDFForPrint(selectedPages, true, loadingToastId)
    } catch (error) {
      console.error('Error generating PDF for print:', error)
      // Reset generating flag on error
      setPdfGenerating(false)
      // Dismiss loading toast on error - try both ref and direct ID
      if (loadingToastIdRef.current) {
                          const toastId = loadingToastIdRef.current
                          if (toastId) {
                            toast.update(toastId, {
                              render: 'Opening print dialog...',
                              type: 'success',
                              isLoading: false,
                              autoClose: 2000,
                            })
                            loadingToastIdRef.current = null
                          }
      }
      if (loadingToastId) {
        toast.dismiss(loadingToastId)
      }
      // Fallback to simple window.print if PDF generation fails
      toast.warning('Falling back to browser print. Some pages may not print correctly.', { autoClose: 3000 })
      window.print()
    }
  }
  
  async function quickPrint() {
    console.log('[PrescriptionMakingPage] Quick print called - cloning pages and opening drawer')
    
    // Show loading message while multipass algorithm runs and pages render
    const loadingToastId = toast.info('Preparing pages for printing...', { 
      autoClose: false,
      closeOnClick: false,
      closeButton: false
    })
    
    // Save prescription data asynchronously in background (non-blocking)
    // This ensures data is saved but doesn't block the print operation
    // Start save in background - don't await it, let it run completely async
    // Suppress toast since this is a background save during quick print
    console.log('[PrescriptionMakingPage] Quick print: Initiating background save...')
    const savePromise = savePrescriptionData(true, true)
      .then(() => {
        console.log('[PrescriptionMakingPage] Quick print: Background save completed successfully')
      })
      .catch(err => {
        console.error('[PrescriptionMakingPage] Quick print: Background save failed:', err)
        // Don't show error toast - save is in background
      })
    
    // Store save promise for potential debugging (optional)
    if (typeof window !== 'undefined') {
      window.__quickPrintSavePromise = savePromise
    }
    
    try {
      const _allPagesAtEntry = Array.from(document.querySelectorAll('.prescription-page'))
      const _visiblePagesAtEntry = _allPagesAtEntry.filter((pageEl) => {
        const s = window.getComputedStyle(pageEl)
        return s.display !== 'none' && s.visibility !== 'hidden'
      })
      const _drawerAncestors = _visiblePagesAtEntry
        .map((pageEl) => pageEl.closest('[class*="MuiDrawer"]'))
        .filter(Boolean)

      // Direct print path: print the exact visible View Prescription surface in-place (same tab).
      const useVisibleSurfaceDirectPrintAtTop = true
      if (useVisibleSurfaceDirectPrintAtTop) {
        // Option B (live render): open drawer and let it render the same
        // prescription component tree, then print from drawer surface.
        setClonedPagesHTML('')
        setQuickPrintDrawerOpen(true)
        toast.info('Preparing print preview...', { autoClose: 1200 })
        toast.dismiss(loadingToastId)
        return

        const _styleTags = Array.from(document.querySelectorAll('style'))
        const _bodyHideStyles = _styleTags.filter((styleTag) => {
          const txt = styleTag.textContent || ''
          return txt.includes('body>*:not(') || txt.includes('body *:not(') || txt.includes('@media print')
        })
        const _hardHideStyles = _styleTags.filter((styleTag) => {
          const txt = styleTag.textContent || ''
          return txt.includes('body>*:not(#') || txt.includes('body *:not(#')
        })

        const _onBeforePrint = () => {
          const _pagesAtPrint = Array.from(document.querySelectorAll('.prescription-page'))
          const _visiblePagesAtPrint = _pagesAtPrint.filter((pageEl) => {
            const s = window.getComputedStyle(pageEl)
            return s.display !== 'none' && s.visibility !== 'hidden' && s.opacity !== '0'
          })
          window.removeEventListener('beforeprint', _onBeforePrint)
        }
        window.addEventListener('beforeprint', _onBeforePrint)

        const _ua = window.navigator?.userAgent || ''
        const _isFirefox = /firefox/i.test(_ua) && !/seamonkey/i.test(_ua)
        const _isSafari = /safari/i.test(_ua) && !/chrome|chromium|crios|fxios|edg/i.test(_ua)
        const _safariPageHeightPx = Math.max(1, Math.floor((A4DpiToDetailsMap?.[96]?.height || 1123) - 1))

        const _mq = window.matchMedia ? window.matchMedia('print') : null
        let _cleanupDone = false
        const _onPrintMediaChange = (evt) => {
          const _pagesAtChange = Array.from(document.querySelectorAll('.prescription-page'))
          const _firstPage = _pagesAtChange[0] || null
          const _secondPage = _pagesAtChange[1] || null
          const _lastPage = _pagesAtChange.length ? _pagesAtChange[_pagesAtChange.length - 1] : null
          const _headerEl = _firstPage?.querySelector('[data-print-header="true"]') || null
          const _bodyEl = _firstPage?.querySelector('[data-prescription-body="true"]') || null
          const _footerEl = _firstPage?.querySelector('[data-print-footer="true"]') || null
          const _a4Roots = Array.from(document.querySelectorAll('[id^="a4-"]'))
          const _ancestorChainSnapshot = (el) => {
            const chain = []
            let cur = el
            let hops = 0
            while (cur && hops < 8) {
              const cs = window.getComputedStyle(cur)
              chain.push({
                tag: cur.tagName,
                id: cur.id || null,
                className: cur.className || null,
                display: cs.display,
                visibility: cs.visibility,
                opacity: cs.opacity,
                position: cs.position,
                overflow: cs.overflow,
                transform: cs.transform,
                contain: cs.contain,
              })
              cur = cur.parentElement
              hops += 1
            }
            return chain
          }
          const _elSnapshot = (el) => {
            if (!el) return null
            const cs = window.getComputedStyle(el)
            const rect = el.getBoundingClientRect()
            return {
              display: cs.display,
              visibility: cs.visibility,
              opacity: cs.opacity,
              position: cs.position,
              overflow: cs.overflow,
              color: cs.color,
              backgroundColor: cs.backgroundColor,
              width: cs.width,
              height: cs.height,
              offsetH: el.offsetHeight,
              scrollH: el.scrollHeight,
              textLen: (el.textContent || '').trim().length,
              rect: {
                x: rect.x,
                y: rect.y,
                w: rect.width,
                h: rect.height,
              },
            }
          }
          const _breakSnapshot = (el) => {
            if (!el) return null
            const cs = window.getComputedStyle(el)
            return {
              className: el.className || null,
              id: el.id || null,
              pageBreakBefore: cs.pageBreakBefore || null,
              pageBreakAfter: cs.pageBreakAfter || null,
              pageBreakInside: cs.pageBreakInside || null,
              breakBefore: cs.breakBefore || null,
              breakAfter: cs.breakAfter || null,
              breakInside: cs.breakInside || null,
              height: cs.height || null,
              minHeight: cs.minHeight || null,
              maxHeight: cs.maxHeight || null,
              zoom: cs.zoom || null,
            }
          }
          const _hasDisplayNoneAncestor = (el) => {
            let cur = el
            let hops = 0
            while (cur && hops < 24) {
              const cs = window.getComputedStyle(cur)
              if (cs.display === 'none' || cs.visibility === 'hidden') {
                return {
                  hasHiddenAncestor: true,
                  atTag: cur.tagName,
                  atId: cur.id || null,
                  atClass: cur.className || null,
                  display: cs.display,
                  visibility: cs.visibility,
                }
              }
              cur = cur.parentElement
              hops += 1
            }
            return { hasHiddenAncestor: false }
          }
          if (!evt?.matches && _mq?.removeEventListener) {
            _mq.removeEventListener('change', _onPrintMediaChange)
            if (!_cleanupDone) {
              _cleanupDone = true
              _cleanupAfterPrint()
            }
          }
        }
        if (_mq?.addEventListener) {
          _mq.addEventListener('change', _onPrintMediaChange)
        }

        // Identify print CSS rules that can hide app root during print.
        const _rootRuleHits = []
        const _rootEl = _visiblePagesAtEntry[0]?.closest('.Root') || document.querySelector('.Root')
        Array.from(document.styleSheets || []).forEach((sheet, sheetIdx) => {
          let rules
          try {
            rules = sheet.cssRules
          } catch (_e) {
            return
          }
          if (!rules) return
          Array.from(rules).forEach((rule) => {
            if (!rule?.media?.mediaText || !`${rule.media.mediaText}`.includes('print')) return
            Array.from(rule.cssRules || []).forEach((subRule) => {
              const selector = subRule?.selectorText || ''
              const display = subRule?.style?.display || ''
              if (!selector) return
              const hitsRoot =
                selector.includes('.Root') ||
                selector.includes('body') ||
                selector.includes('html')
              const hidesDisplay = display.includes('none')
              if (hitsRoot && hidesDisplay) {
                _rootRuleHits.push({
                  sheetIdx,
                  media: rule.media.mediaText,
                  selector,
                  display,
                })
              }
            })
          })
        })

        // Normalize scroll position and hide floating quick-action UI before print.
        const _prePrintWindowScroll = {
          x: window.scrollX || 0,
          y: window.scrollY || 0,
        }
        const _prePrintAncestorScrolls = []
        const _prePrintStyleRestores = []
        const _setTempStyle = (el, prop, value) => {
          _prePrintStyleRestores.push({
            el,
            prop,
            value: el.style.getPropertyValue(prop),
            priority: el.style.getPropertyPriority(prop),
          })
          el.style.setProperty(prop, value, 'important')
        }
        const _firstPage = _visiblePagesAtEntry[0] || null
        if (_firstPage) {
          let cur = _firstPage.parentElement
          let hops = 0
          while (cur && cur !== document.body && hops < 14) {
            const cs = window.getComputedStyle(cur)
            const isScrollable =
              ['auto', 'scroll'].includes(cs.overflowY) ||
              ['auto', 'scroll'].includes(cs.overflow)
            if (isScrollable || cur.scrollTop > 0) {
              _prePrintAncestorScrolls.push({ el: cur, top: cur.scrollTop, left: cur.scrollLeft })
              cur.scrollTop = 0
              cur.scrollLeft = 0
              _setTempStyle(cur, 'overflow', 'visible')
              _setTempStyle(cur, 'overflow-y', 'visible')
              _setTempStyle(cur, 'max-height', 'none')
              _setTempStyle(cur, 'height', 'auto')
            }
            cur = cur.parentElement
            hops += 1
          }
        }
        window.scrollTo(0, 0)

        // Safari: force inline clamp on A4 roots to avoid trailing blank sheet
        // when stylesheet order/specificity keeps them at 1123px in print mode.
        if (_isSafari) {
          const _a4Els = Array.from(document.querySelectorAll('[id^="a4-"]'))
          _a4Els.forEach((el) => {
            _setTempStyle(el, 'height', `${_safariPageHeightPx}px`)
            _setTempStyle(el, 'min-height', `${_safariPageHeightPx}px`)
            _setTempStyle(el, 'max-height', `${_safariPageHeightPx}px`)
            _setTempStyle(el, 'zoom', '0.999')
          })
        }

        const _quickActionHideStyle = document.createElement('style')
        _quickActionHideStyle.setAttribute('data-quick-print-hide-actions', 'true')
        _quickActionHideStyle.textContent = `
          @media print {
            .MuiSpeedDial-root,
            .MuiSpeedDial-actions,
            .MuiSpeedDialAction-staticTooltipLabel,
            .MuiTooltip-popper,
            .MuiTooltip-tooltip,
            [role="tooltip"] {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
            }
          }
        `
        document.head.appendChild(_quickActionHideStyle)

        // Firefox-specific: shrink printable page slightly to avoid
        // 100% scale blank page after each real page.
        let _firefoxScaleStyle = null
        if (_isFirefox) {
          _firefoxScaleStyle = document.createElement('style')
          _firefoxScaleStyle.setAttribute('data-quick-print-firefox-scale-fix', 'true')
          _firefoxScaleStyle.textContent = `
            @media print {
              .prescription-page,
              .prescription-page.preview-page-container {
                zoom: 0.999 !important;
              }
            }
          `
          document.head.appendChild(_firefoxScaleStyle)
        }

        // Safari-specific: prevent double page-break generation from nested A4 wrappers.
        let _safariBreakStyle = null
        if (_isSafari) {
          _safariBreakStyle = document.createElement('style')
          _safariBreakStyle.setAttribute('data-quick-print-safari-break-fix', 'true')
          _safariBreakStyle.textContent = `
            @media print {
              html body .PMPPrintWrapper .prescription-page,
              html body .PMPPrintWrapper div.prescription-page,
              html body .PMPPrintWrapper .prescription-page.preview-page-container,
              html body .prescription-page,
              html body div.prescription-page,
              html body .prescription-page.preview-page-container {
                break-after: page !important;
                page-break-after: always !important;
                break-inside: avoid !important;
                page-break-inside: avoid !important;
                zoom: 0.999 !important;
                height: ${_safariPageHeightPx}px !important;
                min-height: ${_safariPageHeightPx}px !important;
                max-height: ${_safariPageHeightPx}px !important;
              }
              html body .PMPPrintWrapper .prescription-page:last-of-type,
              html body .prescription-page:last-of-type {
                break-after: auto !important;
                page-break-after: auto !important;
              }
              [id^="a4-"],
              [id^="a4-"] > * {
                break-after: auto !important;
                page-break-after: auto !important;
                break-before: auto !important;
                page-break-before: auto !important;
                height: ${_safariPageHeightPx}px !important;
                min-height: ${_safariPageHeightPx}px !important;
                max-height: ${_safariPageHeightPx}px !important;
                zoom: 0.999 !important;
              }
            }
          `
          document.head.appendChild(_safariBreakStyle)
        }

        const _cleanupAfterPrint = () => {
          if (_quickActionHideStyle.parentNode) {
            _quickActionHideStyle.parentNode.removeChild(_quickActionHideStyle)
          }
          if (_firefoxScaleStyle?.parentNode) {
            _firefoxScaleStyle.parentNode.removeChild(_firefoxScaleStyle)
          }
          if (_safariBreakStyle?.parentNode) {
            _safariBreakStyle.parentNode.removeChild(_safariBreakStyle)
          }
          while (_prePrintStyleRestores.length > 0) {
            const _m = _prePrintStyleRestores.pop()
            if (!_m || !_m.el) continue
            if (_m.value) {
              _m.el.style.setProperty(_m.prop, _m.value, _m.priority || '')
            } else {
              _m.el.style.removeProperty(_m.prop)
            }
          }
          _prePrintAncestorScrolls.forEach((s) => {
            s.el.scrollTop = s.top || 0
            s.el.scrollLeft = s.left || 0
          })
          window.scrollTo(_prePrintWindowScroll.x, _prePrintWindowScroll.y)
        }
        const _onAfterPrint = () => {
          window.removeEventListener('afterprint', _onAfterPrint)
          if (!_cleanupDone) {
            _cleanupDone = true
            _cleanupAfterPrint()
          }
        }
        window.addEventListener('afterprint', _onAfterPrint)
        toast.info('Opening print preview...', { autoClose: 1200 })
        await new Promise((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              try {
                window.print()
              } catch (e) {
                console.error('[PrescriptionMakingPage Quick Print] direct window.print failed:', e)
                if (!_cleanupDone) {
                  _cleanupDone = true
                  _cleanupAfterPrint()
                }
              }
              resolve()
            })
          })
        })
        toast.dismiss(loadingToastId)
        return
      }

      // Get A4 dimensions from printSettings (same as PDF generation)
      const currentDpiData = A4DpiToDetailsMap?.[printSettings?.dpi] || A4DpiToDetailsMap[96]
      const widthOfA4Page = currentDpiData?.width || 794
      const heightOfA4Page = currentDpiData?.height || 1123 // A4 height for footer positioning
      
      // STEP 1: Scroll through document FIRST to force all content to render
      // CRITICAL: Dimensions are measured using getBoundingClientRect() which returns 0 
      // for elements outside viewport. Scrolling forces all elements into viewport so 
      // dimensions are measured correctly, which triggers multipass algorithm to recalculate.
      toast.update(loadingToastId, {
        render: 'Scrolling to measure all content...',
        type: 'info',
        autoClose: false
      })
      
      const scrollToRenderAllPages = () => {
        return new Promise((resolve) => {
          const _initialPageCount = document.querySelectorAll('.prescription-page').length
          
          const maxScroll = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.offsetHeight,
            document.body.clientHeight,
            document.documentElement.clientHeight
          )
          
          // Scroll through the document in steps to force all pages to render
          let currentScroll = 0
          const scrollStep = window.innerHeight * 0.8 // Scroll 80% of viewport height each step
          const scrollDelay = 150 // Wait 150ms between scrolls
          
          const scrollStepByStep = () => {
            if (currentScroll < maxScroll) {
              currentScroll = Math.min(currentScroll + scrollStep, maxScroll)
              window.scrollTo({ top: currentScroll, behavior: 'auto' })
              
              window.setTimeout(() => {
                const _pageCount = document.querySelectorAll('.prescription-page').length
                scrollStepByStep()
              }, scrollDelay)
            } else {
              // Finished scrolling, wait a bit more for final render
              window.setTimeout(() => {
                const _finalPageCount = document.querySelectorAll('.prescription-page').length
                
                // Scroll back to top
                window.scrollTo({ top: 0, behavior: 'auto' })
                window.requestAnimationFrame(() => {
                  window.requestAnimationFrame(() => {
                    window.setTimeout(() => {
                      const _afterScrollBackPageCount = document.querySelectorAll('.prescription-page').length
                      resolve()
                    }, 200)
                  })
                })
              }, 500) // Wait 500ms after reaching bottom
            }
          }
          
          // Start scrolling
          scrollStepByStep()
        })
      }
      await scrollToRenderAllPages()
      
      // STEP 2: Wait for dimensions to update after scrolling
      // useElementDimensions hook listens to scroll events and will refresh dimensions
      // We need to wait for React to re-render with updated dimensions
      await new Promise((resolve) => {
        // Wait for scroll events to trigger dimension updates
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.setTimeout(() => {
              resolve()
            }, 500) // Give time for dimension hooks to update
          })
        })
      })
      
      // STEP 3: Wait for multipass algorithm to complete with updated dimensions
      // The algorithm should recalculate after dimensions update, potentially increasing totalPages
      const waitForMultipassAlgorithm = () => {
        return new Promise((resolve) => {
          const initialTotalPages = totalPages
          const maxWait = 10000 // Max 10 seconds
          const startTime = Date.now()
          let lastTotalPages = initialTotalPages
          let stableCount = 0
          const requiredStableChecks = 3
          
          const checkTotalPages = () => {
            const currentTotalPages = totalPages
            
            // If totalPages increased, dimensions updated and algorithm recalculated
            if (currentTotalPages > initialTotalPages) {
              toast.update(loadingToastId, {
                render: `Found ${currentTotalPages} page(s). Rendering pages...`,
                type: 'info',
                autoClose: false
              })
              resolve()
              return
            }
            
            // If totalPages is stable and > 0, algorithm has completed
            if (currentTotalPages > 0) {
              if (currentTotalPages === lastTotalPages) {
                stableCount++
                if (stableCount >= requiredStableChecks) {
                  toast.update(loadingToastId, {
                    render: `Found ${currentTotalPages} page(s). Rendering pages...`,
                    type: 'info',
                    autoClose: false
                  })
                  resolve()
                  return
                }
              } else {
                stableCount = 0 // Reset if changed
              }
            }
            
            lastTotalPages = currentTotalPages
            
            // Check if we've exceeded max wait time
            if (Date.now() - startTime > maxWait) {
              // Max wait time reached, proceeding with available pages
              toast.update(loadingToastId, {
                render: `Proceeding with ${currentTotalPages} page(s)...`,
                type: 'warning',
                autoClose: false
              })
              resolve()
              return
            }
            
            // Continue checking
            window.requestAnimationFrame(() => {
              window.setTimeout(checkTotalPages, 200) // Check every 200ms
            })
          }
          
          // Start checking after a short delay
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              window.setTimeout(checkTotalPages, 300)
            })
          })
        })
      }
      
      await waitForMultipassAlgorithm()
      
      // STEP 4: Wait for all pages to be rendered in the DOM
      // Use totalPages from PrescriptionViewComponent to know how many pages should exist
      const waitForAllPages = () => {
        return new Promise((resolve) => {
          const expectedPageCount = totalPages || 0
          
          if (expectedPageCount === 0) {
            // totalPages is 0, cannot wait for pages. Proceeding anyway.
            resolve()
            return
          }
          
          let lastPageCount = 0
          let stableCount = 0
          const requiredStableChecks = 3
          const maxWait = 5000 // Max 5 seconds
          
          const startTime = Date.now()
          
          const checkPages = () => {
            const currentPages = Array.from(document.querySelectorAll('.prescription-page')).filter(page => {
              const pageStyle = window.getComputedStyle(page)
              if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
                return false
              }
              let parent = page.parentElement
              while (parent && parent !== document.body) {
                const style = window.getComputedStyle(parent)
                if (style.display === 'none' || style.visibility === 'hidden') {
                  return false
                }
                if (parent.classList.contains('MuiDrawer-root') && style.display === 'none') {
                  return false
                }
                parent = parent.parentElement
              }
              return true
            })
            
            const currentCount = currentPages.length
            
            // Check if we have the expected number of pages
            if (currentCount >= expectedPageCount && currentCount === lastPageCount) {
              stableCount++
              if (stableCount >= requiredStableChecks) {
                resolve()
                return
              }
            } else if (currentCount < expectedPageCount) {
              stableCount = 0 // Reset if we don't have enough pages yet
            } else if (currentCount === lastPageCount && currentCount > 0) {
              // If count is stable but less than expected, still wait a bit more
              stableCount++
              if (stableCount >= requiredStableChecks * 2) {
                resolve()
                return
              }
            } else {
              stableCount = 0 // Reset if count changed
            }
            
            lastPageCount = currentCount
            
            // Check if we've exceeded max wait time
            if (Date.now() - startTime > maxWait) {
              resolve()
              return
            }
            
            // Continue checking
            window.requestAnimationFrame(() => {
              window.setTimeout(checkPages, 200) // Check every 200ms
            })
          }
          
          // Start checking after a short delay to allow React to render
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              window.setTimeout(checkPages, 300)
            })
          })
        })
      }
      
      toast.update(loadingToastId, {
        render: `Waiting for all ${totalPages || '?'} page(s) to render...`,
        type: 'info',
        autoClose: false
      })
      await waitForAllPages()
      
      // Dismiss loading toast
      toast.dismiss(loadingToastId)
      
      // Get all prescription pages - use same logic as PDF generation
      let allPages = Array.from(document.querySelectorAll('.prescription-page'))
      
      // Filter out pages from hidden containers (same logic as PDF generation)
      allPages = allPages.filter(page => {
        // Check if page itself is visible
        const pageStyle = window.getComputedStyle(page)
        if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
          return false
        }
        
        // Check if page is in a visible container (not hidden drawer/modal)
        let parent = page.parentElement
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent)
          // Skip if parent is hidden
          if (style.display === 'none' || style.visibility === 'hidden') {
            return false
          }
          // Skip if parent is a closed drawer (MUI Drawer with display: none)
          if (parent.classList.contains('MuiDrawer-root') && style.display === 'none') {
            return false
          }
          parent = parent.parentElement
        }
        return true
      })
      
      // Deduplicate by page number - same logic as PDF generation
      const pagesByNumber = new Map()
      allPages.forEach(page => {
        const pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page')
        if (pageNum) {
          // If we already have a page with this number, prefer the one not in PMPPrintWrapper
          // (main page takes priority over Rx drawer)
          if (!pagesByNumber.has(pageNum)) {
            pagesByNumber.set(pageNum, page)
          } else {
            const existingPage = pagesByNumber.get(pageNum)
            const isExistingInDrawer = existingPage.closest('.PMPPrintWrapper') !== null
            const isCurrentInDrawer = page.closest('.PMPPrintWrapper') !== null
            
            // Prefer main page over drawer page
            if (isExistingInDrawer && !isCurrentInDrawer) {
              pagesByNumber.set(pageNum, page)
            }
          }
        } else {
          // If no page number, add it (shouldn't happen, but handle it)
          pagesByNumber.set(`no-number-${pagesByNumber.size}`, page)
        }
      })
      
      allPages = Array.from(pagesByNumber.values())
      
      // Sort pages by data-page-number to ensure correct order (same as PDF generation)
      allPages.sort((a, b) => {
        const pageNumA = parseInt(a.getAttribute('data-page-number') || a.getAttribute('data-pdf-page')) || 0
        const pageNumB = parseInt(b.getAttribute('data-page-number') || b.getAttribute('data-pdf-page')) || 0
        return pageNumA - pageNumB
      })
      
      console.log(`[PrescriptionMakingPage Quick Print] Found ${allPages.length} unique page(s) to process`)
      
      // Log page numbers to verify we're getting all pages
      const pageNumbers = allPages.map(page => {
        const pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page') || 'unknown'
        return pageNum
      })
      console.log(`[PrescriptionMakingPage Quick Print] Page numbers found: ${pageNumbers.join(', ')}`)
      
      // Log detailed info about each page
      allPages.forEach((page, _idx) => {
        const _pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page') || 'unknown'
        const _rect = page.getBoundingClientRect()
        const _scrollHeight = page.scrollHeight
      })
      
      // Verify we have all expected pages (check for gaps in page numbers)
      const numericPageNumbers = pageNumbers
        .map(num => parseInt(num))
        .filter(num => !isNaN(num))
        .sort((a, b) => a - b)
      
      if (numericPageNumbers.length > 1) {
        for (let i = 0; i < numericPageNumbers.length - 1; i++) {
          if (numericPageNumbers[i + 1] - numericPageNumbers[i] > 1) {
            console.warn(`[PrescriptionMakingPage Quick Print] WARNING: Gap detected in page numbers! Missing pages between ${numericPageNumbers[i]} and ${numericPageNumbers[i + 1]}`)
          }
        }
      }
      
      if (!allPages || allPages.length === 0) {
        toast.error('No prescription pages found to print', { autoClose: 3000 })
        return
      }
      
      // Wait for multipass algorithm to complete and layout to settle
      // The multipass algorithm needs time to fit chunks properly across pages
      // We wait for multiple animation frames and check for layout stability
      const waitForLayoutSettlement = () => {
        return new Promise((resolve) => {
          // Track page heights to detect when layout is stable
          let previousHeights = []
          let stableCount = 0
          const requiredStableChecks = 3 // Require 3 consecutive stable checks
          
          const checkStability = () => {
            // Re-query pages to ensure we have all pages (pages might be added during layout)
            const currentAllPages = Array.from(document.querySelectorAll('.prescription-page')).filter(page => {
              const pageStyle = window.getComputedStyle(page)
              if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
                return false
              }
              let parent = page.parentElement
              while (parent && parent !== document.body) {
                const style = window.getComputedStyle(parent)
                if (style.display === 'none' || style.visibility === 'hidden') {
                  return false
                }
                if (parent.classList.contains('MuiDrawer-root') && style.display === 'none') {
                  return false
                }
                parent = parent.parentElement
              }
              return true
            })
            
            // Update allPages if we found more pages
            if (currentAllPages.length > allPages.length) {
              allPages = currentAllPages
              // Re-deduplicate and sort
              const pagesByNumber = new Map()
              allPages.forEach(page => {
                const pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page')
                if (pageNum) {
                  if (!pagesByNumber.has(pageNum)) {
                    pagesByNumber.set(pageNum, page)
                  } else {
                    const existingPage = pagesByNumber.get(pageNum)
                    const isExistingInDrawer = existingPage.closest('.PMPPrintWrapper') !== null
                    const isCurrentInDrawer = page.closest('.PMPPrintWrapper') !== null
                    if (isExistingInDrawer && !isCurrentInDrawer) {
                      pagesByNumber.set(pageNum, page)
                    }
                  }
                } else {
                  pagesByNumber.set(`no-number-${pagesByNumber.size}`, page)
                }
              })
              allPages = Array.from(pagesByNumber.values())
              allPages.sort((a, b) => {
                const pageNumA = parseInt(a.getAttribute('data-page-number') || a.getAttribute('data-pdf-page')) || 0
                const pageNumB = parseInt(b.getAttribute('data-page-number') || b.getAttribute('data-pdf-page')) || 0
                return pageNumA - pageNumB
              })
            }
            
            // Get current heights of all prescription pages
            // Use scrollHeight instead of getBoundingClientRect().height for pages outside viewport
            const currentHeights = allPages.map((page, _idx) => {
              const rect = page.getBoundingClientRect()
              const scrollHeight = page.scrollHeight
              // Use scrollHeight if rect.height is 0 or very small (likely outside viewport)
              const effectiveHeight = rect.height > 10 ? rect.height : scrollHeight
              return {
                height: effectiveHeight,
                scrollHeight: scrollHeight
              }
            })
            
            // Check if heights are stable (same as previous check)
            const isStable = previousHeights.length === currentHeights.length &&
              previousHeights.every((prev, idx) => 
                prev && currentHeights[idx] &&
                Math.abs(prev.height - currentHeights[idx].height) < 1 && // Allow 1px tolerance
                Math.abs(prev.scrollHeight - currentHeights[idx].scrollHeight) < 1
              )
            
            if (isStable) {
              stableCount++
              if (stableCount >= requiredStableChecks) {
                console.log('[PrescriptionMakingPage Quick Print] Layout stable, multipass algorithm complete')
                resolve()
                return
              }
            } else {
              stableCount = 0 // Reset counter if layout changed
            }
            
            previousHeights = currentHeights
            
            // Continue checking until stable or timeout
            window.requestAnimationFrame(() => {
              window.setTimeout(checkStability, 100) // Check every 100ms
            })
          }
          
          // Start checking after initial render
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              // Initial delay to allow multipass algorithm to start
              window.setTimeout(() => {
                checkStability()
              }, 200)
            })
          })
          
          // Timeout after 2 seconds to prevent infinite waiting
          window.setTimeout(() => {
            if (stableCount < requiredStableChecks) {
              console.warn('[PrescriptionMakingPage Quick Print] Layout stability timeout, proceeding anyway')
              resolve()
            }
          }, 2000)
        })
      }
      
      // Wait for layout to settle before cloning
      console.log('[PrescriptionMakingPage Quick Print] Waiting for multipass algorithm to complete...')
      await waitForLayoutSettlement()
      
      // Re-query pages after layout settlement to ensure we have all pages
      // (Some pages might have been created during layout settlement)
      let finalPages = Array.from(document.querySelectorAll('.prescription-page'))
      finalPages = finalPages.filter(page => {
        const pageStyle = window.getComputedStyle(page)
        if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
          return false
        }
        let parent = page.parentElement
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent)
          if (style.display === 'none' || style.visibility === 'hidden') {
            return false
          }
          if (parent.classList.contains('MuiDrawer-root') && style.display === 'none') {
            return false
          }
          parent = parent.parentElement
        }
        return true
      })
      
      // Update allPages if we found more pages after layout settlement
      if (finalPages.length > allPages.length) {
        console.log(`[PrescriptionMakingPage Quick Print] Found additional pages after layout settlement: ${finalPages.length} total (was ${allPages.length})`)
        allPages = finalPages
        
        // Re-deduplicate and sort
        const pagesByNumber = new Map()
        allPages.forEach(page => {
          const pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page')
          if (pageNum) {
            if (!pagesByNumber.has(pageNum)) {
              pagesByNumber.set(pageNum, page)
            } else {
              const existingPage = pagesByNumber.get(pageNum)
              const isExistingInDrawer = existingPage.closest('.PMPPrintWrapper') !== null
              const isCurrentInDrawer = page.closest('.PMPPrintWrapper') !== null
              if (isExistingInDrawer && !isCurrentInDrawer) {
                pagesByNumber.set(pageNum, page)
              }
            }
          } else {
            pagesByNumber.set(`no-number-${pagesByNumber.size}`, page)
          }
        })
        allPages = Array.from(pagesByNumber.values())
        allPages.sort((a, b) => {
          const pageNumA = parseInt(a.getAttribute('data-page-number') || a.getAttribute('data-pdf-page')) || 0
          const pageNumB = parseInt(b.getAttribute('data-page-number') || b.getAttribute('data-pdf-page')) || 0
          return pageNumA - pageNumB
        })
      }

      // Direct print path: print the exact visible View Prescription surface in-place (same tab),
      // instead of reconstructing/cloning into popup/iframe.
      // This preserves header/footer/body design parity with what users see on /l/* view.
      const useVisibleSurfaceDirectPrint = true
      if (useVisibleSurfaceDirectPrint) {
        toast.info('Opening print preview...', { autoClose: 1200 })
        await new Promise((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              try {
                window.print()
              } catch (e) {
                console.error('[PrescriptionMakingPage Quick Print] direct window.print failed:', e)
              }
              resolve()
            })
          })
        })
        return
      }
      
      // Helper function to copy computed styles to cloned element
      // Excludes width/height to allow explicit page width constraints
      const copyComputedStyles = (original, cloned) => {
        const computed = window.getComputedStyle(original)
        const styleProps = [
          'display', 'visibility', 'opacity', 'position',
          'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
          'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
          'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
          'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
          'textAlign', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'color',
          'backgroundColor', 'background', 'backgroundImage', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat',
          'boxSizing', 'flexDirection', 'flexWrap', 'justifyContent', 'alignItems', 'alignContent', 'flex', 'flexGrow', 'flexShrink', 'flexBasis',
          'gridTemplateColumns', 'gridTemplateRows', 'gridGap', 'gridColumn', 'gridRow',
          'textDecoration', 'textTransform', 'letterSpacing', 'wordSpacing',
          'verticalAlign', 'whiteSpace', 'overflow', 'overflowX', 'overflowY',
          'zIndex', 'transform', 'transformOrigin',
          // Table specific
          'borderCollapse', 'borderSpacing', 'tableLayout', 'captionSide',
          // Additional visual properties
          'boxShadow', 'textShadow', 'outline', 'outlineWidth', 'outlineStyle', 'outlineColor',
          'listStyle', 'listStyleType', 'listStylePosition',
        ]
        
        styleProps.forEach(prop => {
          const value = computed.getPropertyValue(prop)
          // Only copy if value exists and is meaningful
          if (value && value !== 'none' && value !== 'normal' && value !== 'auto' && value !== '0px') {
            // Don't use !important for most styles to allow CSS overrides
            cloned.style.setProperty(prop, value)
          }
        })
        
        // Explicitly preserve borders if they exist (critical for prescription pages)
        const borderWidth = computed.borderWidth
        const borderStyle = computed.borderStyle
        const borderColor = computed.borderColor
        if (borderWidth && borderWidth !== '0px' && borderStyle && borderStyle !== 'none') {
          cloned.style.border = `${borderWidth} ${borderStyle} ${borderColor}`
        }
        
        // Preserve all border properties individually as well
        if (computed.borderTopWidth && computed.borderTopWidth !== '0px') {
          cloned.style.borderTop = `${computed.borderTopWidth} ${computed.borderTopStyle} ${computed.borderTopColor}`
        }
        if (computed.borderRightWidth && computed.borderRightWidth !== '0px') {
          cloned.style.borderRight = `${computed.borderRightWidth} ${computed.borderRightStyle} ${computed.borderRightColor}`
        }
        if (computed.borderBottomWidth && computed.borderBottomWidth !== '0px') {
          cloned.style.borderBottom = `${computed.borderBottomWidth} ${computed.borderBottomStyle} ${computed.borderBottomColor}`
        }
        if (computed.borderLeftWidth && computed.borderLeftWidth !== '0px') {
          cloned.style.borderLeft = `${computed.borderLeftWidth} ${computed.borderLeftStyle} ${computed.borderLeftColor}`
        }
      }
      
      // Extract all CSS from the document - get actual CSS rules from stylesheets
      const extractAllCSS = () => {
        let css = ''
        
        // Extract from style tags
        const styleTags = document.querySelectorAll('style')
        styleTags.forEach(style => {
          if (style.textContent) {
            css += style.textContent + '\n'
          }
        })
        
        // Extract CSS rules from all stylesheets (including external ones if accessible)
        try {
          for (let i = 0; i < document.styleSheets.length; i++) {
            try {
              const sheet = document.styleSheets[i]
              // Check if we can access cssRules (will throw SecurityError for cross-origin sheets)
              let cssRules = null
              try {
                cssRules = sheet.cssRules
              } catch (e) {
                // Cross-origin stylesheet - cannot access cssRules
                if (sheet.href) {
                  // External stylesheet - will be included as link tag
                  css += `/* External stylesheet: ${sheet.href} */\n`
                }
                continue
              }
              
              if (cssRules) {
                for (let j = 0; j < cssRules.length; j++) {
                  try {
                    css += cssRules[j].cssText + '\n'
                  } catch (e) {
                    // Skip individual rule if inaccessible
                  }
                }
              } else if (sheet.href) {
                // External stylesheet - will be included as link tag
                css += `/* External stylesheet: ${sheet.href} */\n`
              }
            } catch (e) {
              // Cross-origin or inaccessible stylesheet - silently skip
              // Don't log warnings for expected cross-origin errors
            }
          }
        } catch (e) {
          console.warn('Error extracting stylesheets:', e)
        }
        
        return css
      }
      
      const allDocumentCSS = extractAllCSS()
      
      // Pre-process CSS: neutralise every page-break-*:always / break-*:page rule from app stylesheets.
      // These fire inside cloned .prescription-page elements during print (e.g. A4PageContainer has
      // @media print { page-break-after: always }), producing blank pages.
      // We keep only our explicit .prescription-page rules for page-break control.
      const printSafeDocumentCSS = allDocumentCSS
        .replace(/(page-break-after)\s*:\s*always/gi, '$1: auto')
        .replace(/(break-after)\s*:\s*(page|column)/gi, '$1: auto')
        .replace(/(page-break-before)\s*:\s*always/gi, '$1: auto')
        .replace(/(break-before)\s*:\s*(page|column)/gi, '$1: auto')
      
      // Helper function to convert image to base64 - same as PDF generation
      const imageToBase64 = async (url, originalImgElement = null) => {
        // Strategy 1: Use already-loaded image from DOM if available and safe
        if (originalImgElement && originalImgElement.complete && originalImgElement.naturalHeight !== 0) {
          try {
            const imgUrl = new URL(originalImgElement.src, window.location.href)
            const isSameOrigin = imgUrl.origin === window.location.origin
            
            if (isSameOrigin || originalImgElement.crossOrigin !== null) {
              const canvas = document.createElement('canvas')
              canvas.width = originalImgElement.naturalWidth || originalImgElement.width
              canvas.height = originalImgElement.naturalHeight || originalImgElement.height
              const ctx = canvas.getContext('2d')
              ctx.drawImage(originalImgElement, 0, 0)
              return canvas.toDataURL('image/png')
            }
          } catch (e) {
            console.warn('Could not get image from DOM element, trying other methods:', url)
          }
        }
        
        // Strategy 2: Fetch with CORS headers
        try {
          const response = await fetch(url, { 
            mode: 'cors', 
            credentials: 'omit',
            headers: { 'Accept': 'image/*' }
          })
          if (response.ok) {
            const blob = await response.blob()
            return new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onloadend = () => resolve(reader.result)
              reader.onerror = () => reject(new Error('FileReader failed'))
              reader.readAsDataURL(blob)
            })
          }
        } catch (fetchError) {
          console.warn('Fetch with CORS failed, trying Image element:', url)
        }
        
        // Strategy 3: Use Image element
        return new Promise((resolve) => {
          const img = new Image()
          if (originalImgElement && originalImgElement.crossOrigin !== null) {
            img.crossOrigin = originalImgElement.crossOrigin
          } else {
            try {
              const imgUrl = new URL(url, window.location.href)
              if (imgUrl.origin !== window.location.origin) {
                img.crossOrigin = 'anonymous'
              }
            } catch (e) {
              img.crossOrigin = 'anonymous'
            }
          }
          
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas')
              canvas.width = img.width
              canvas.height = img.height
              const ctx = canvas.getContext('2d')
              ctx.drawImage(img, 0, 0)
              resolve(canvas.toDataURL('image/png'))
            } catch (e) {
              console.warn('Canvas tainted, using original URL:', url)
              resolve(url)
            }
          }
          img.onerror = () => {
            console.warn('Image load failed, using original URL:', url)
            resolve(url)
          }
          img.src = url
        })
      }
      
      // Clone pages - same approach as PDF generation
      const clonedPages = []
      for (let idx = 0; idx < allPages.length; idx++) {
        const pageElement = allPages[idx]
        if (!pageElement) continue
        
        
        const cloned = pageElement.cloneNode(true)
        
        // Remove hide-on-print elements (same as PDF generation)
        const hideElements = cloned.querySelectorAll('.hide-on-print-only, .hide-on-print')
        hideElements.forEach(el => el.remove())
        
        // Convert images to base64 - same as PDF generation
        const images = cloned.querySelectorAll('img')
        for (const clonedImg of images) {
          const src = clonedImg.getAttribute('src')
          if (src && !src.startsWith('data:')) {
            try {
              // Try to find the original image element
              let originalImg = null
              try {
                originalImg = pageElement.querySelector(`img[src="${src}"]`)
                if (!originalImg) {
                  const allImgs = Array.from(pageElement.querySelectorAll('img'))
                  originalImg = allImgs.find(img => {
                    try {
                      return img.src === src || 
                             img.getAttribute('src') === src ||
                             new URL(img.src, window.location.href).href === new URL(src, window.location.href).href
                    } catch (e) {
                      return img.src === src || img.getAttribute('src') === src
                    }
                  })
                }
              } catch (e) {
                console.warn('Error finding original image element:', e)
              }
              
              const base64 = await imageToBase64(src, originalImg)
              if (base64 && base64.startsWith('data:')) {
                clonedImg.src = base64
              }
            } catch (error) {
              console.error('Failed to convert image to base64:', src, error)
            }
          }
        }
        
        // Handle background images - same as PDF generation
        const elementsWithBgImages = cloned.querySelectorAll('[style*="background-image"]')
        for (const el of elementsWithBgImages) {
          const style = el.getAttribute('style')
          const bgMatch = style.match(/background-image:\s*url\(['"]?([^'"]+)['"]?\)/)
          if (bgMatch && bgMatch[1] && !bgMatch[1].startsWith('data:')) {
            try {
              const base64 = await imageToBase64(bgMatch[1])
              if (base64 && base64.startsWith('data:')) {
                el.style.backgroundImage = `url(${base64})`
              }
            } catch (error) {
              console.warn('Failed to convert background image to base64:', bgMatch[1], error)
            }
          }
        }
        
        // Copy computed styles from original to cloned (recursively for all elements)
        const copyStylesRecursive = (orig, clone) => {
          copyComputedStyles(orig, clone)
          const origChildren = orig.children
          const cloneChildren = clone.children
          for (let i = 0; i < origChildren.length && i < cloneChildren.length; i++) {
            copyStylesRecursive(origChildren[i], cloneChildren[i])
          }
        }
        // Hypothesis H-clone-styles: recursive computed-style copying over-constrains flex layout in popup
        // and shifts body off-canvas in print preview. Skip deep copy and rely on original CSS + targeted
        // page-level inline styles below.
        
        // Remove padding/margins from A4 containers that cause content to be narrower
        const a4Containers = cloned.querySelectorAll('.A4PageContainer, .A4PageMeasureComponentWrapperContainer, [id^="a4-"]')
        a4Containers.forEach(container => {
          container.style.padding = '0'
          container.style.margin = '0'
          container.style.width = '100%'
          container.style.maxWidth = '100%'
          container.style.minWidth = '100%'
          container.style.boxSizing = 'border-box'
          container.style.height = 'auto'
          container.style.maxHeight = 'none'
          container.style.minHeight = 'auto'
        })
        
        // Ensure all child elements maintain proper dimensions to prevent extra pages
        // But preserve their computed styles first
        const allChildren = cloned.querySelectorAll('*')
        allChildren.forEach(child => {
          const childComputed = window.getComputedStyle(child)
          // Ensure max-width respects page width, but don't override if it's already smaller
          if (childComputed.maxWidth && parseFloat(childComputed.maxWidth) > widthOfA4Page) {
            child.style.maxWidth = '100%'
          }
          // Ensure box-sizing is border-box for proper width calculations
          if (!child.style.boxSizing) {
            child.style.boxSizing = 'border-box'
          }
        })
        
        // Style the cloned page for print - preserve borders from original
        // CRITICAL: Use flex layout and fixed A4 height to maintain footer at bottom
        const originalComputed = window.getComputedStyle(pageElement)
        cloned.style.width = `${widthOfA4Page}px`
        cloned.style.maxWidth = `${widthOfA4Page}px`
        cloned.style.minWidth = `${widthOfA4Page}px`
        cloned.style.height = `${heightOfA4Page - 1}px` // 1px less than A4DpiToDetailsMap height to fit within physical A4 (1122.52px) without needing zoom
        cloned.style.minHeight = `${heightOfA4Page - 1}px`
        cloned.style.maxHeight = `${heightOfA4Page - 1}px`
        cloned.style.margin = '0 auto'
        cloned.style.padding = '0'
        cloned.style.display = 'flex' // Use flex for footer positioning
        cloned.style.flexDirection = 'column'
        cloned.style.position = 'relative'
        cloned.style.boxSizing = 'border-box'
        cloned.style.overflow = 'visible' // avoid WebKit blank-page bug (overflow:hidden + fixed height + page-break = extra blank page)
        cloned.style.pageBreakAfter = idx < allPages.length - 1 ? 'always' : 'auto'
        cloned.style.breakAfter = idx < allPages.length - 1 ? 'page' : 'auto'
        cloned.style.pageBreakInside = 'avoid'
        cloned.style.pageBreakBefore = 'auto'
        cloned.style.clear = 'both'
        cloned.style.float = 'none'
        
        // Also apply flex to direct children to maintain layout
        const directChildren = cloned.children
        for (let i = 0; i < directChildren.length; i++) {
          const child = directChildren[i]
          child.style.display = 'flex'
          child.style.flexDirection = 'column'
          child.style.flex = '1'
          child.style.minHeight = '0'
        }
        
        // Preserve blank header space div - ensure it's not collapsed by flex
        const blankHeaderSpaces = cloned.querySelectorAll('.blank-header-space, [class*="PmpHeaderContainer"] > div[style*="height"], [class*="PmpHeaderContainer"] > div[data-header-height]')
        blankHeaderSpaces.forEach(blankSpace => {
          const style = blankSpace.getAttribute('style') || ''
          const dataHeight = blankSpace.getAttribute('data-header-height')
          const computedStyle = window.getComputedStyle(blankSpace)
          
          // Get height from data attribute, style attribute, or computed style
          let actualHeight = '120px'
          if (dataHeight) {
            actualHeight = `${dataHeight}px`
          } else if (style.includes('height:')) {
            const heightMatch = style.match(/height:\s*([^;]+)/)
            if (heightMatch) {
              actualHeight = heightMatch[1].trim()
            }
          } else if (computedStyle.height && computedStyle.height !== 'auto') {
            actualHeight = computedStyle.height
          }
          
          // Extract numeric value for flex
          const heightNum = parseFloat(actualHeight) || 120
          
          // Only target divs that are actually blank header space (have height and are empty or have minimal content)
          if ((style.includes('height') || dataHeight) && (!blankSpace.textContent || blankSpace.textContent.trim() === '' || blankSpace.querySelector('span[style*="opacity: 0"]'))) {
            blankSpace.style.height = actualHeight
            blankSpace.style.minHeight = actualHeight
            blankSpace.style.maxHeight = actualHeight
            blankSpace.style.width = '100%'
            blankSpace.style.display = 'block'
            blankSpace.style.visibility = 'visible'
            blankSpace.style.flexShrink = '0'
            blankSpace.style.flexGrow = '0'
            blankSpace.style.flex = `0 0 ${actualHeight}`
            blankSpace.style.backgroundColor = 'transparent'
            blankSpace.style.border = '1px solid transparent'
            blankSpace.style.margin = '0'
            blankSpace.style.padding = '0'
            blankSpace.style.overflow = 'hidden'
            blankSpace.style.position = 'relative'
            blankSpace.classList.add('blank-header-space')
            blankSpace.setAttribute('data-header-height', heightNum.toString())
            
            // Ensure the invisible spacer span exists with correct height
            const existingSpan = blankSpace.querySelector('span[style*="opacity: 0"]')
            if (existingSpan) {
              existingSpan.style.height = actualHeight
              existingSpan.style.lineHeight = actualHeight
            } else {
              const spacer = document.createElement('span')
              spacer.style.cssText = `display: inline-block; height: ${actualHeight}; width: 1px; opacity: 0; visibility: hidden; position: absolute; font-size: 1px; line-height: ${actualHeight};`
              spacer.textContent = '\u200B' // Zero-width space
              blankSpace.appendChild(spacer)
            }
          }
        })
        
        // Preserve blank footer space div and ensure footer sticks to bottom
        const blankFooterSpaces = cloned.querySelectorAll('.blank-footer-space, [data-print-footer="true"] > div[style*="height"], [class*="PmpV2FooterContainer"] > div[style*="height"], [class*="PmpV2FooterContainer"] > div[data-footer-height]')
        blankFooterSpaces.forEach(blankSpace => {
          const style = blankSpace.getAttribute('style') || ''
          const dataHeight = blankSpace.getAttribute('data-footer-height')
          const computedStyle = window.getComputedStyle(blankSpace)
          
          // Get height from data attribute, style attribute, or computed style
          let actualHeight = '120px'
          if (dataHeight) {
            actualHeight = `${dataHeight}px`
          } else if (style.includes('height:')) {
            const heightMatch = style.match(/height:\s*([^;]+)/)
            if (heightMatch) {
              actualHeight = heightMatch[1].trim()
            }
          } else if (computedStyle.height && computedStyle.height !== 'auto') {
            actualHeight = computedStyle.height
          }
          
          // Extract numeric value for flex
          const heightNum = parseFloat(actualHeight) || 120
          
          // Only target divs that are actually blank footer space (have height and are empty or have minimal content)
          if ((style.includes('height') || dataHeight) && (!blankSpace.textContent || blankSpace.textContent.trim() === '' || blankSpace.querySelector('span[style*="opacity: 0"]'))) {
            blankSpace.style.height = actualHeight
            blankSpace.style.minHeight = actualHeight
            blankSpace.style.maxHeight = actualHeight
            blankSpace.style.width = '100%'
            blankSpace.style.display = 'block'
            blankSpace.style.visibility = 'visible'
            blankSpace.style.flex = `0 0 ${actualHeight}`
            blankSpace.style.flexShrink = '0'
            blankSpace.style.flexGrow = '0'
            blankSpace.style.backgroundColor = 'transparent'
            blankSpace.style.border = '1px solid transparent'
            blankSpace.style.margin = '0'
            blankSpace.style.padding = '0'
            blankSpace.style.overflow = 'hidden'
            blankSpace.style.position = 'relative'
            blankSpace.classList.add('blank-footer-space')
            blankSpace.setAttribute('data-footer-height', heightNum.toString())
            
            // Ensure the invisible spacer span exists with correct height
            const existingSpan = blankSpace.querySelector('span[style*="opacity: 0"]')
            if (existingSpan) {
              existingSpan.style.height = actualHeight
              existingSpan.style.lineHeight = actualHeight
            } else {
              const spacer = document.createElement('span')
              spacer.style.cssText = `display: inline-block; height: ${actualHeight}; width: 1px; opacity: 0; visibility: hidden; position: absolute; font-size: 1px; line-height: ${actualHeight};`
              spacer.textContent = '\u200B' // Zero-width space
              blankSpace.appendChild(spacer)
            }
          }
        })
        
        // Ensure footer containers and spacers maintain flex layout to push footer to bottom
        const footerContainers = cloned.querySelectorAll('[data-print-footer="true"]')
        footerContainers.forEach(footerContainer => {
          footerContainer.style.display = 'flex'
          footerContainer.style.flexDirection = 'column'
          footerContainer.style.flexShrink = '0'
          footerContainer.style.marginTop = 'auto'
        })
        
        const spacers = cloned.querySelectorAll('[data-print-spacer="true"], .footer-spacer-rectangle')
        spacers.forEach(spacer => {
          spacer.style.flex = '1 1 auto'
          spacer.style.minHeight = '0'
          spacer.style.display = 'block'
          spacer.style.width = '100%'
          spacer.style.backgroundColor = 'transparent'
          spacer.style.border = 'none'
          spacer.style.margin = '0'
          spacer.style.padding = '0'
          spacer.style.boxSizing = 'border-box'
          spacer.style.visibility = 'visible'
          spacer.style.opacity = '1'
          spacer.classList.add('footer-spacer-rectangle')
        })
        
        // The on-screen page frame (preview border + radius + shadow) must NOT print — it shows
        // as a line around the page. Force it off on the cloned page (don't copy the source border).
        cloned.style.border = 'none'
        cloned.style.borderRadius = '0'
        cloned.style.boxShadow = 'none'
        cloned.style.outline = 'none'

        cloned.classList.add('prescription-page')
        
        clonedPages.push(cloned)
      }
      
      
      // Build HTML for all pages - continuous HTML like the working window approach
      // Trim any whitespace to prevent extra blank pages
      const pagesHTML = clonedPages.map(page => page.outerHTML).join('').trim()
      
      // Verify all drug advice rows are captured
      // Count drug advice rows in cloned pages using multiple strategies
      let totalDrugAdviceRowsInCloned = 0
      const drugAdviceRowDetails = []
      
      clonedPages.forEach((clonedPage, pageIdx) => {
        const pageText = clonedPage.textContent || ''
        
        // Strategy 1: Count table rows (for table format)
        const tableRows = clonedPage.querySelectorAll('tr:not(.isHeaderRow)')
        const drugTableRows = Array.from(tableRows).filter(row => {
          const rowText = row.textContent || ''
          // Check if row contains drug-related content (not just headers)
          return rowText.length > 10 && (
            rowText.includes('Rx') || 
            row.querySelector('[class*="drug"]') ||
            row.querySelector('[class*="Drug"]')
          )
        })
        
        // Strategy 2: Count list items (for list format)
        const listItems = clonedPage.querySelectorAll('[class*="drug-advice"], [class*="DrugAdvice"]')
        
        // Strategy 3: Count by text patterns (drug names, dosages, etc.)
        const drugNameMatches = (pageText.match(/Rx|Prescription|Drug/gi) || []).length
        
        // Use the maximum count as the estimate
        const rowsInPage = Math.max(drugTableRows.length, listItems.length, Math.floor(drugNameMatches / 2))
        
        if (rowsInPage > 0 || pageText.includes('Rx')) {
          drugAdviceRowDetails.push({ page: pageIdx + 1, rows: rowsInPage, hasRxContent: pageText.includes('Rx') })
          totalDrugAdviceRowsInCloned += rowsInPage
          console.log(`[PrescriptionMakingPage Quick Print] Page ${pageIdx + 1}: Found ${rowsInPage} drug advice row(s) (table: ${drugTableRows.length}, list: ${listItems.length})`)
        }
      })
      
      // Compare with source data if available
      if (drugsAdvice && Array.isArray(drugsAdvice)) {
        const filteredDrugsAdvice = drugsAdvice.filter((item) => item?.drug?.label)
        console.log(`[PrescriptionMakingPage Quick Print] Source data: ${filteredDrugsAdvice.length} drug advice row(s) with drug labels`)
        console.log(`[PrescriptionMakingPage Quick Print] Cloned pages: ${totalDrugAdviceRowsInCloned} drug advice row(s) found across ${drugAdviceRowDetails.length} page(s)`)
        
        // More accurate verification: check if drug names appear in cloned content
        // Use case-insensitive matching and normalize whitespace to reduce false positives
        const allClonedText = clonedPages.map(p => p.textContent || '').join(' ').toLowerCase().replace(/\s+/g, ' ')
        let missingDrugs = []
        
        filteredDrugsAdvice.forEach((drug, idx) => {
          const drugName = drug?.drug?.label
          if (drugName) {
            // Normalize the drug name for comparison
            const normalizedDrugName = drugName.toLowerCase().replace(/\s+/g, ' ').trim()
            // Try exact match first
            let found = allClonedText.includes(normalizedDrugName)
            // If not found, try partial match (first 10 chars) to handle truncation
            if (!found && normalizedDrugName.length > 10) {
              const partialName = normalizedDrugName.substring(0, 10)
              found = allClonedText.includes(partialName)
            }
            // If still not found, try matching just alphanumeric characters
            if (!found) {
              const alphanumericName = normalizedDrugName.replace(/[^a-z0-9]/g, '')
              const alphanumericText = allClonedText.replace(/[^a-z0-9]/g, '')
              found = alphanumericName.length >= 3 && alphanumericText.includes(alphanumericName)
            }
            if (!found) {
              missingDrugs.push({ index: idx + 1, name: drugName })
            }
          }
        })
        
        if (missingDrugs.length > 0) {
          // Only show warning if significant number of drugs are missing (> 20% or > 2)
          const significantMissing = missingDrugs.length > 2 || missingDrugs.length / filteredDrugsAdvice.length > 0.2
          console.warn(`[PrescriptionMakingPage Quick Print] ${missingDrugs.length} drug advice row(s) may not be found in text:`)
          missingDrugs.forEach(missing => {
            console.warn(`[PrescriptionMakingPage Quick Print]   - Row ${missing.index}: ${missing.name}`)
          })
          if (significantMissing) {
            toast.warning(`Warning: ${missingDrugs.length} drug row(s) may be missing from print. Check console for details.`, { autoClose: 5000 })
          } else {
            console.log(`[PrescriptionMakingPage Quick Print] Minor mismatch (${missingDrugs.length} rows) - may be false positive due to text rendering differences`)
          }
        } else if (totalDrugAdviceRowsInCloned < filteredDrugsAdvice.length) {
          console.warn(`[PrescriptionMakingPage Quick Print] WARNING: Row count mismatch. Expected ${filteredDrugsAdvice.length}, found ${totalDrugAdviceRowsInCloned}. All drug names appear present, but count differs.`)
        } else {
          console.log(`[PrescriptionMakingPage Quick Print] ✓ All ${filteredDrugsAdvice.length} drug advice rows verified in cloned pages`)
        }
      }
      
      // Check if running in PWA mode - PWAs often block window.open()
      const isPwaMode = isPWA()
      console.log('[PrescriptionMakingPage Quick Print] PWA mode:', isPwaMode)
      
      // Helper function to create and use hidden iframe for printing (PWA fallback)
      const printUsingIframe = () => {
        // Create a hidden iframe for printing in PWA mode
        const printIframe = document.createElement('iframe')
        // Off-screen but NOT zero-size: Safari's print engine requires a non-zero viewport
        // to correctly apply @page sizes, zoom, and page-break calculations.
        // A 0×0 iframe is treated like display:none and breaks all print layout in Safari.
        printIframe.setAttribute('style', 'position:fixed;left:-9999px;top:0;width:794px;height:1px;border:0;pointer-events:none;')
        document.body.appendChild(printIframe)
        
        // Write HTML to iframe
        printIframe.contentDocument.open()
        printIframe.contentDocument.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Prescription Print</title>
              <meta charset="utf-8">
              ${Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(link => {
                try {
                  const href = link.href
                  if (href && !href.startsWith('data:')) {
                    return `<link rel="stylesheet" href="${href}">`
                  }
                } catch (e) {
                  return ''
                }
                return ''
              }).filter(Boolean).join('\n')}
              <style>
                ${printSafeDocumentCSS}
                /* Our rules come AFTER printSafeDocumentCSS so they win on equal specificity */
                html, body { margin: 0 !important; padding: 0 !important; }
                body { background: white !important; }
                @page { size: A4 portrait; margin: 0mm; }
                @media print {
                  html { margin: 0 !important; padding: 0 !important; }
                  body { margin: 0 !important; padding: 0 !important; }
                  * { -webkit-print-color-adjust: ${colorMode === 'bw' ? 'economy' : 'exact'} !important; print-color-adjust: ${colorMode === 'bw' ? 'economy' : 'exact'} !important; }
                  ${colorMode === 'bw' ? 'body { filter: grayscale(100%) !important; }' : ''}
                  /* Safari/Firefox blank-page fix: printSafeDocumentCSS includes
                     "div.prescription-page { zoom: 1 !important }" (specificity 0,1,1) from
                     PrescriptionViewWithPrintCSS.jsx, which beats ".prescription-page { zoom: 0.999 }" (0,1,0).
                     "html body .prescription-page" has specificity 0,1,2 which beats 0,1,1.
                     1123px × 0.999 = 1121.877px < A4 (1122.52px) → fits cleanly, no blank page. */
                  html body .prescription-page, html body div.prescription-page { zoom: 0.999 !important; }
                  /* Only .prescription-page inline styles drive page breaks; strip any from inner elements */
                  .prescription-page * { page-break-after: auto !important; break-after: auto !important; page-break-before: auto !important; break-before: auto !important; }
                  .prescription-page:last-child, body > .prescription-page:last-of-type { page-break-after: auto !important; break-after: auto !important; }
                  .prescription-page [data-print-header="true"] { overflow: visible !important; }
                  .prescription-page [data-print-patient-info="true"] {
                    display: flex !important;
                    flex-wrap: wrap !important;
                    align-items: flex-start !important;
                    width: 100% !important;
                    max-width: 100% !important;
                    min-width: 0 !important;
                    white-space: normal !important;
                  }
                  .prescription-page [data-print-patient-info="true"] * {
                    white-space: normal !important;
                    overflow-wrap: anywhere !important;
                    word-break: break-word !important;
                  }
                }
              </style>
            </head>
            <body style="margin: 0 !important; padding: 0 !important; overflow: visible !important; height: auto !important; max-height: none !important;">
              ${pagesHTML}
            </body>
          </html>
        `)
        printIframe.contentDocument.close()
        
        // Wait for iframe to load and stylesheets to load, then print
        // Reduced timeout for faster print dialog opening
        const waitForIframeStylesheets = (callback, maxWait = 1500) => {
          let _callbackCallCount = 0
          const _guardedCallback = () => {
            _callbackCallCount++
            if (_callbackCallCount > 1) return
            callback()
          }
          const startTime = Date.now()
          const checkStylesheets = () => {
            try {
              const stylesheets = printIframe.contentDocument.querySelectorAll('link[rel="stylesheet"]')
              let loadedCount = 0
              let totalCount = stylesheets.length
              
              if (totalCount === 0) {
                _guardedCallback()
                return
              }
              
              stylesheets.forEach(link => {
                try {
                  const sheet = link.sheet
                  if (sheet && sheet.cssRules) {
                    loadedCount++
                  } else if (link.onload) {
                    loadedCount++
                  } else {
                    link.onload = () => {
                      loadedCount++
                      if (loadedCount === totalCount || Date.now() - startTime > maxWait) {
                        _guardedCallback()
                      }
                    }
                    link.onerror = () => {
                      loadedCount++
                      if (loadedCount === totalCount || Date.now() - startTime > maxWait) {
                        _guardedCallback()
                      }
                    }
                  }
                } catch (e) {
                  loadedCount++
                }
              })
              
              if (loadedCount === totalCount || Date.now() - startTime > maxWait) {
                _guardedCallback()
              } else {
                setTimeout(checkStylesheets, 100)
              }
            } catch (e) {
              // If we can't access iframe document, proceed anyway
              _guardedCallback()
            }
          }
          
          printIframe.onload = () => {
            setTimeout(checkStylesheets, 100)
          }
          
          // Fallback if onload doesn't fire
          setTimeout(() => {
            checkStylesheets()
          }, 500)
        }
        
        waitForIframeStylesheets(() => {
          // Ensure prescription pages have correct width
          try {
            const body = printIframe.contentDocument.body
            const prescriptionPages = body.querySelectorAll('.prescription-page')
            prescriptionPages.forEach(page => {
              page.style.width = `${widthOfA4Page}px`
              page.style.maxWidth = `${widthOfA4Page}px`
              page.style.minWidth = `${widthOfA4Page}px`
              page.style.margin = '0 auto'
              page.style.boxSizing = 'border-box'

              // Preserve blank footer spaces so footer alignment stays consistent.
              const blankFooterSpaces = page.querySelectorAll('.blank-footer-space, [data-print-footer="true"] > div[style*="height"], [class*="PmpV2FooterContainer"] > div[style*="height"], [class*="PmpV2FooterContainer"] > div[data-footer-height]')
              blankFooterSpaces.forEach(blankSpace => {
                const style = blankSpace.getAttribute('style') || ''
                const dataHeight = blankSpace.getAttribute('data-footer-height')
                const computedStyle = printIframe.contentWindow.getComputedStyle(blankSpace)
                let actualHeight = '120px'
                if (dataHeight) {
                  actualHeight = `${dataHeight}px`
                } else if (style.includes('height:')) {
                  const heightMatch = style.match(/height:\s*([^;]+)/)
                  if (heightMatch) actualHeight = heightMatch[1].trim()
                } else if (computedStyle.height && computedStyle.height !== 'auto') {
                  actualHeight = computedStyle.height
                }
                if (style.includes('height') || dataHeight) {
                  blankSpace.style.height = actualHeight
                  blankSpace.style.minHeight = actualHeight
                  blankSpace.style.maxHeight = actualHeight
                  blankSpace.style.flex = `0 0 ${actualHeight}`
                  blankSpace.style.flexShrink = '0'
                  blankSpace.style.flexGrow = '0'
                  blankSpace.style.display = 'block'
                  blankSpace.style.visibility = 'visible'
                }
              })

            })
          } catch (e) {
            console.warn('Could not access iframe document for styling:', e)
          }
          
          // Wait for content to be fully rendered and painted before printing
          // This ensures the browser's print dialog doesn't show loading preview
          const waitForIframeRender = () => {
            return new Promise((resolve) => {
              try {
                let attempts = 0
                const maxAttempts = 20
                
                const checkReady = () => {
                  attempts++
                  
                  try {
                    const doc = printIframe.contentDocument
                    const body = doc.body
                    
                    if (doc.readyState === 'complete' && body && body.children.length > 0) {
                      const pages = body.querySelectorAll('.prescription-page')
                      if (pages.length > 0) {
                        // Check if pages have content
                        let hasContent = false
                        for (let i = 0; i < Math.min(pages.length, 3); i++) {
                          const page = pages[i]
                          const rect = page.getBoundingClientRect()
                          if (rect.height > 100) {
                            hasContent = true
                            break
                          }
                        }
                        
                        if (hasContent) {
                          // Wait for paint cycles
                          let paintCycles = 0
                          const waitForPaint = () => {
                            window.requestAnimationFrame(() => {
                              paintCycles++
                              if (paintCycles >= 2) {
                                window.setTimeout(() => {
                                  console.log(`[PrescriptionMakingPage Quick Print] Iframe content fully rendered (${pages.length} pages), triggering print...`)
                                  resolve()
                                }, 150)
                              } else {
                                waitForPaint()
                              }
                            })
                          }
                          waitForPaint()
                          return
                        }
                      }
                    }
                    
                    if (attempts < maxAttempts) {
                      window.setTimeout(checkReady, 100)
                    } else {
                      console.log(`[PrescriptionMakingPage Quick Print] Iframe render timeout after ${attempts} attempts, proceeding...`)
                      resolve()
                    }
                  } catch (e) {
                    console.warn('[PrescriptionMakingPage Quick Print] Error checking iframe render state:', e)
                    window.setTimeout(() => resolve(), 200)
                  }
                }
                
                window.setTimeout(() => {
                  checkReady()
                }, 100)
                
                window.setTimeout(() => {
                  console.log(`[PrescriptionMakingPage Quick Print] Iframe fallback timeout, proceeding with print...`)
                  resolve()
                }, 3000)
              } catch (e) {
                console.warn('[PrescriptionMakingPage Quick Print] Error accessing iframe:', e)
                window.setTimeout(() => resolve(), 200)
              }
            })
          }
          
          // Wait for render before printing
          waitForIframeRender().then(() => {
            try {
              // Count pages in iframe before printing
              let _pagesInIframe = 0
              try {
                const iframeBody = printIframe.contentDocument?.body
                if (iframeBody) {
                  _pagesInIframe = iframeBody.querySelectorAll('.prescription-page').length
                }
              } catch (_e) {
                // Could not count pages in iframe
              }
              console.log(`[PrescriptionMakingPage Quick Print] Triggering print from iframe (PWA mode) with ${allPages.length} pages...`)
              if (!printIframe || !printIframe.contentWindow) {
                console.error('[PrescriptionMakingPage Quick Print] iframe or contentWindow is null at print time - skipping duplicate print call')
                return
              }
              printIframe.contentWindow.print()
              
              // Clean up iframe after print dialog closes
              const cleanup = () => {
                if (printIframe && printIframe.parentNode) {
                  printIframe.parentNode.removeChild(printIframe)
                }
                window.removeEventListener('afterprint', cleanup)
              }
              window.addEventListener('afterprint', cleanup)
              // Fallback cleanup after 5 seconds
              window.setTimeout(cleanup, 5000)
            } catch (e) {
              console.error('Error printing from iframe:', e)
              toast.error('Failed to print. Please try again.', { autoClose: 3000 })
              // Clean up on error
              if (printIframe && printIframe.parentNode) {
                printIframe.parentNode.removeChild(printIframe)
              }
            }
          })
        })
        
      }
      
      // Try window.open() first (works in most cases, even some PWAs)
      // If it fails or is blocked, fall back to iframe approach
      // The iframe approach works even when popups are blocked
      let printWindow = null
      let popupBlocked = false
      
      try {
        printWindow = window.open('', '_blank')
        
        // Check if popup was blocked
        // If window.open() returns null or undefined, popup is definitely blocked
        if (!printWindow) {
          popupBlocked = true
          console.log('[PrescriptionMakingPage Quick Print] window.open() returned null/undefined - popup blocked')
        } else {
          // Some browsers return a window object but immediately close it or block access
          // Check if window is accessible
          try {
            // Try to access window properties - if this throws, popup is likely blocked
            const isClosed = typeof printWindow.closed !== 'undefined' ? printWindow.closed : false
            
            if (isClosed) {
              popupBlocked = true
              console.log('[PrescriptionMakingPage Quick Print] window.open() returned closed window - popup blocked')
            } else {
              // Try to access document - if this fails, popup is likely blocked
              // Note: document might not be immediately available, so we check with a small delay
              window.setTimeout(() => {
                try {
                  if (!printWindow || printWindow.closed || !printWindow.document) {
                    // Window was closed or document is inaccessible - use iframe fallback
                    console.log('[PrescriptionMakingPage Quick Print] Window closed or inaccessible after delay - using iframe fallback')
                    printUsingIframe()
                  }
                } catch (e) {
                  // Cannot access window - use iframe fallback
                  console.log('[PrescriptionMakingPage Quick Print] Cannot access window after delay - using iframe fallback:', e)
                  printUsingIframe()
                }
              }, 50)
            }
          } catch (e) {
            // If we can't access window properties at all, popup is likely blocked
            popupBlocked = true
            console.log('[PrescriptionMakingPage Quick Print] Cannot access window properties - popup blocked:', e)
          }
        }
      } catch (e) {
        popupBlocked = true
        console.warn('[PrescriptionMakingPage Quick Print] window.open() threw error - popup blocked:', e)
      }
      
      // If window.open() failed or was blocked, use iframe approach immediately
      // This works even when popups are blocked
      if (popupBlocked || !printWindow) {
        console.log('[PrescriptionMakingPage Quick Print] Popup blocked or window.open() failed, using iframe fallback')
        printUsingIframe()
        return
      }
      
      // Verify window is accessible before proceeding
      try {
        if (printWindow.closed) {
          console.log('[PrescriptionMakingPage Quick Print] Window is closed, using iframe fallback')
          printUsingIframe()
          return
        }
        // Try to access document - if this throws, use iframe fallback
        if (!printWindow.document) {
          console.log('[PrescriptionMakingPage Quick Print] Cannot access window.document, using iframe fallback')
          printUsingIframe()
          return
        }
      } catch (e) {
        console.log('[PrescriptionMakingPage Quick Print] Error verifying window access, using iframe fallback:', e)
        printUsingIframe()
        return
      }
      
      // window.open() succeeded - use new window approach
      // This ensures all pages are continuous and overflow is not hidden
      
      // currentDpiData and widthOfA4Page are already declared above, reuse them
      
      // Write HTML to new window - same structure as working examples
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Prescription Print</title>
            <meta charset="utf-8">
            ${Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(link => {
              try {
                const href = link.href
                if (href && !href.startsWith('data:')) {
                  return `<link rel="stylesheet" href="${href}">`
                }
              } catch (e) {
                return ''
              }
              return ''
            }).filter(Boolean).join('\n')}
            <style>
              /* Include all CSS from original document */
              ${printSafeDocumentCSS}
              
              /* Ensure all prescription page styles are preserved */
              .prescription-page,
              .prescription-page * {
                box-sizing: border-box;
              }
              
              ${colorMode === 'bw' 
                ? `
                  @media print {
                    * {
                      -webkit-print-color-adjust: economy !important;
                      print-color-adjust: economy !important;
                      color-adjust: economy !important;
                    }
                    body {
                      filter: grayscale(100%) !important;
                      -webkit-filter: grayscale(100%) !important;
                    }
                  }
                `
                : `
                  @media print {
                    * {
                      -webkit-print-color-adjust: exact !important;
                      print-color-adjust: exact !important;
                      color-adjust: exact !important;
                    }
                  }
                `
              }
              @page {
                size: A4 portrait;
                margin: 0mm;
              }
              * {
                box-sizing: border-box;
              }
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                height: auto !important;
                min-height: auto !important;
                max-height: none !important;
                background: white !important;
                overflow: visible !important;
                overflow-x: visible !important;
                overflow-y: visible !important;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              }
              /* Removed blanket height:auto rule that was breaking flex layout for footer positioning */
              p, span {
                max-height: none !important;
                overflow: visible !important;
              }
              .prescription-page {
                display: flex !important;
                flex-direction: column !important;
                visibility: visible !important;
                opacity: 1 !important;
                break-inside: avoid !important;
                page-break-inside: avoid !important;
                break-after: page !important;
                page-break-after: always !important;
                page-break-before: auto !important;
                margin: 0 auto !important;
                padding: 0 !important;
                width: ${widthOfA4Page}px !important;
                max-width: ${widthOfA4Page}px !important;
                min-width: ${widthOfA4Page}px !important;
                height: ${heightOfA4Page - 1}px !important;
                min-height: ${heightOfA4Page - 1}px !important;
                max-height: ${heightOfA4Page - 1}px !important;
                box-sizing: border-box !important;
                position: relative !important;
                clear: both !important;
                float: none !important;
                overflow: hidden !important;
              }
              /* Ensure flex containers maintain their layout for footer positioning */
              .prescription-page > div {
                display: flex !important;
                flex-direction: column !important;
                flex: 1 !important;
                min-height: 0 !important;
              }
              .prescription-page > div > div {
                display: flex !important;
                flex-direction: column !important;
                flex: 1 !important;
                min-height: 0 !important;
              }
              .prescription-page [data-print-header="true"] {
                overflow: visible !important;
              }
              .prescription-page [data-print-patient-info="true"] {
                display: flex !important;
                flex-wrap: wrap !important;
                align-items: flex-start !important;
                width: 100% !important;
                max-width: 100% !important;
                min-width: 0 !important;
                white-space: normal !important;
              }
              .prescription-page [data-print-patient-info="true"] * {
                white-space: normal !important;
                overflow-wrap: anywhere !important;
                word-break: break-word !important;
              }
              /* CRITICAL: Preserve blank header space - must come after flex rules */
              /* Height values are set via inline styles from JavaScript - don't hardcode here */
              .prescription-page .blank-header-space,
              .prescription-page [class*="PmpHeaderContainer"] > div[data-header-height],
              .prescription-page [class*="PmpHeaderContainer"] > div.blank-header-space {
                display: block !important;
                visibility: visible !important;
                /* height, min-height, max-height, and flex-basis are set via inline styles */
                /* Fallback minimum height to ensure visibility if inline style is missing or 0 */
                min-height: 120px !important;
                width: 100% !important;
                flex-shrink: 0 !important;
                flex-grow: 0 !important;
                box-sizing: border-box !important;
                overflow: hidden !important;
                position: relative !important;
                background-color: transparent !important;
                border: 1px solid transparent !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              /* Ensure blank header space span is preserved */
              /* Height is set via inline styles from JavaScript */
              .prescription-page .blank-header-space > span {
                display: inline-block !important;
                /* height and line-height are set via inline styles */
                width: 1px !important;
                opacity: 0 !important;
                visibility: hidden !important;
                position: absolute !important;
              }
              /* Prevent blank header space from being affected by parent flex */
              .prescription-page [class*="PmpHeaderContainer"] {
                display: flex !important;
                flex-direction: column !important;
              }
              .prescription-page [class*="PmpHeaderContainer"] > .blank-header-space {
                /* flex-basis, height, min-height, max-height are set via inline styles */
                flex-shrink: 0 !important;
                flex-grow: 0 !important;
              }
                /* Flexible spacer to push footer to bottom - empty rectangle to fill space */
                .prescription-page [data-print-spacer="true"],
                .prescription-page .footer-spacer-rectangle {
                  flex: 1 1 auto !important;
                  min-height: 0 !important;
                  display: block !important;
                  width: 100% !important;
                  background-color: transparent !important;
                  border: none !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  box-sizing: border-box !important;
                  visibility: visible !important;
                  opacity: 1 !important;
                }
                /* Footer should be pushed to bottom via flex - target by data attribute */
                .prescription-page [data-print-footer="true"],
                .prescription-page [style*="margin-top: auto"] {
                  flex-shrink: 0 !important;
                  margin-top: auto !important;
                  display: flex !important;
                  flex-direction: column !important;
                }
                /* CRITICAL: Preserve blank footer space and ensure it sticks to bottom */
                /* Height values are set via inline styles from JavaScript - don't hardcode here */
                .prescription-page .blank-footer-space,
                .prescription-page [data-print-footer="true"] > .blank-footer-space,
                .prescription-page [class*="PmpV2FooterContainer"] > div[data-footer-height],
                .prescription-page [class*="PmpV2FooterContainer"] > div.blank-footer-space {
                  display: block !important;
                  visibility: visible !important;
                  /* height, min-height, max-height, and flex-basis are set via inline styles */
                  width: 100% !important;
                  flex-shrink: 0 !important;
                  flex-grow: 0 !important;
                  box-sizing: border-box !important;
                  overflow: hidden !important;
                  position: relative !important;
                  background-color: transparent !important;
                  border: 1px solid transparent !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }
                /* Ensure blank footer space span is preserved */
                /* Height and line-height are set via inline styles from JavaScript */
                .prescription-page .blank-footer-space > span {
                  display: inline-block !important;
                  /* height and line-height are set via inline styles */
                  width: 1px !important;
                  opacity: 0 !important;
                  visibility: hidden !important;
                  position: absolute !important;
                  font-size: 1px !important;
                }
                /* Ensure footer container maintains flex layout and pushes footer to bottom */
                .prescription-page [class*="PmpV2FooterContainer"],
                .prescription-page [data-print-footer="true"] {
                  display: flex !important;
                  flex-direction: column !important;
                  flex-shrink: 0 !important;
                  margin-top: auto !important;
                }
                /* Ensure body content stays within page borders */
              .prescription-page > * {
                max-width: 100% !important;
                box-sizing: border-box !important;
              }
              .prescription-page:last-child {
                break-after: auto !important;
                page-break-after: auto !important;
              }
              /* Additional safeguard: ensure last element in body doesn't cause page break */
              body > .prescription-page:last-of-type {
                break-after: auto !important;
                page-break-after: auto !important;
                margin-bottom: 0 !important;
              }
              /* Remove padding/margins from A4 containers but preserve flex layout */
              .prescription-page .A4PageContainer,
              .prescription-page .A4PageMeasureComponentWrapperContainer,
              .prescription-page [id^="a4-"] {
                padding: 0 !important;
                margin: 0 !important;
                width: 100% !important;
                max-width: 100% !important;
                min-width: 100% !important;
                box-sizing: border-box !important;
                display: flex !important;
                flex-direction: column !important;
                flex: 1 !important;
                min-height: 0 !important;
                height: 100% !important;
                overflow: hidden !important;
              }
              /* Ensure parent containers of spacer are flex containers */
              .prescription-page [data-print-spacer="true"],
              .prescription-page .footer-spacer-rectangle {
                flex: 1 1 auto !important;
                min-height: 0 !important;
                display: block !important;
                width: 100% !important;
                background-color: transparent !important;
                border: none !important;
                margin: 0 !important;
                padding: 0 !important;
                box-sizing: border-box !important;
                visibility: visible !important;
                opacity: 1 !important;
              }
              /* Ensure all elements respect page width */
              .prescription-page * {
                max-width: 100% !important;
                box-sizing: border-box !important;
              }
              /* Ensure tables are visible and properly styled */
              table {
                display: table !important;
                visibility: visible !important;
                opacity: 1 !important;
                width: 100% !important;
                border-collapse: collapse !important;
                page-break-inside: avoid !important;
                margin: 0 !important;
                padding: 0 !important;
              }
              table tr {
                display: table-row !important;
                visibility: visible !important;
                opacity: 1 !important;
              }
              table td,
              table th {
                display: table-cell !important;
                visibility: visible !important;
                opacity: 1 !important;
              }
              @page {
                size: A4 portrait;
                margin: 0mm;
                -webkit-print-color-adjust: ${colorMode === 'bw' ? 'economy' : 'exact'} !important;
                print-color-adjust: ${colorMode === 'bw' ? 'economy' : 'exact'} !important;
                color-adjust: ${colorMode === 'bw' ? 'economy' : 'exact'} !important;
              }
              /* Prevent extra blank pages */
              body::after {
                display: none !important;
                content: '' !important;
              }
              /* Ensure no content after last page causes extra page */
              body > *:last-child {
                page-break-after: auto !important;
              }
              /* Safari/Firefox blank-page fix: "div.prescription-page { zoom: 1 !important }" (specificity 0,1,1)
                 in printSafeDocumentCSS beats ".prescription-page { zoom: 0.999 }" (0,1,0).
                 "html body .prescription-page" (0,1,2) beats it. 1123px × 0.999 = 1121.877px < A4 (1122.52px). */
              @media print {
                html body .prescription-page, html body div.prescription-page { zoom: 0.999 !important; }
                .prescription-page * {
                  page-break-after: auto !important;
                  break-after: auto !important;
                  page-break-before: auto !important;
                  break-before: auto !important;
                }
              }
            </style>
          <!-- Auto-trigger print from inside the new window so Safari doesn't block it.
               Safari blocks printWindow.print() called from the parent window (cross-window call),
               but allows window.print() called from within the window's own JS context.
               We poll window._rxReadyToPrint (set by parent after waitForRender resolves) so
               the dialog only opens after content is fully rendered. -->
          <script>
            var _rxPrinted = false;
            function _rxDoPrint() {
              if (_rxPrinted) return;
              _rxPrinted = true;
              try { window.print(); } catch(e) {
              }
            }
            function _rxCheckReady() {
              if (window._rxReadyToPrint) {
                _rxDoPrint();
              } else {
                setTimeout(_rxCheckReady, 100);
              }
            }
            if (document.readyState === 'complete') {
              _rxCheckReady();
            } else {
              window.addEventListener('load', function() {
                _rxCheckReady();
              });
            }
            // Absolute fallback: fire after 12s if parent never signals
            setTimeout(function() {
              _rxDoPrint();
            }, 12000);
          </script>
          </head>
          <body style="margin: 0 !important; padding: 0 !important; overflow: visible !important; height: auto !important; max-height: none !important;">
            ${pagesHTML}
          </body>
        </html>
      `)
      printWindow.document.close()
      
      // Verify what was written to the print window
      try {
        const _pagesInWindow = printWindow.document.body.querySelectorAll('.prescription-page').length
      } catch (_e) {
        // Could not verify print window content
      }
      
      // Wait for content to load, CSS to apply, and stylesheets to load before printing
      // Reduced timeout for faster print dialog opening
      const waitForStylesheets = (callback, maxWait = 1500) => {
        const startTime = Date.now()
        const checkStylesheets = () => {
          const stylesheets = printWindow.document.querySelectorAll('link[rel="stylesheet"]')
          let loadedCount = 0
          let totalCount = stylesheets.length
          
          if (totalCount === 0) {
            // No external stylesheets, proceed immediately
            callback()
            return
          }
          
          stylesheets.forEach(link => {
            try {
              // Check if stylesheet is loaded by trying to access its sheet
              const sheet = link.sheet
              if (sheet && sheet.cssRules) {
                loadedCount++
              } else if (link.onload) {
                // Already has onload handler, count as loaded
                loadedCount++
              } else {
                // Add onload handler
                link.onload = () => {
                  loadedCount++
                  if (loadedCount === totalCount || Date.now() - startTime > maxWait) {
                    callback()
                  }
                }
                link.onerror = () => {
                  loadedCount++ // Count as "loaded" even if failed
                  if (loadedCount === totalCount || Date.now() - startTime > maxWait) {
                    callback()
                  }
                }
              }
            } catch (e) {
              // Cross-origin stylesheet, count as loaded
              loadedCount++
            }
          })
          
          // If all loaded or timeout, proceed
          if (loadedCount === totalCount || Date.now() - startTime > maxWait) {
            callback()
          } else {
            // Check again after a short delay
            window.setTimeout(checkStylesheets, 50) // Reduced from 100ms to 50ms
          }
        }
        
        checkStylesheets()
      }
      
      let _popupSetupRan = false
      printWindow.onload = () => {
        if (_popupSetupRan) return
        _popupSetupRan = true
        // Ensure body allows full content flow (like working examples)
        const body = printWindow.document.body
        const html = printWindow.document.documentElement
        
        body.style.overflow = 'visible'
        body.style.height = 'auto'
        body.style.maxHeight = 'none'
        body.style.minHeight = 'auto'
        html.style.overflow = 'visible'
        html.style.height = 'auto'
        html.style.maxHeight = 'none'
        html.style.minHeight = 'auto'
        
        // Wait for stylesheets to load before applying styles and printing
        waitForStylesheets(() => {
          // Ensure prescription pages have correct width and borders
          const prescriptionPages = body.querySelectorAll('.prescription-page')
          prescriptionPages.forEach(page => {
            // Enforce page width
            page.style.width = `${widthOfA4Page}px`
            page.style.maxWidth = `${widthOfA4Page}px`
            page.style.minWidth = `${widthOfA4Page}px`
            page.style.margin = '0 auto'
            page.style.boxSizing = 'border-box'

            // CRITICAL: Preserve blank header spaces in print window
            const blankHeaderSpaces = page.querySelectorAll('.blank-header-space, [class*="PmpHeaderContainer"] > div[style*="height"], [class*="PmpHeaderContainer"] > div[data-header-height]')
            blankHeaderSpaces.forEach(blankSpace => {
              const style = blankSpace.getAttribute('style') || ''
              const dataHeight = blankSpace.getAttribute('data-header-height')
              const computedStyle = printWindow.getComputedStyle(blankSpace)
              
              // Get height from data attribute, style attribute, or computed style
              let actualHeight = '120px'
              if (dataHeight) {
                actualHeight = `${dataHeight}px`
              } else if (style.includes('height:')) {
                const heightMatch = style.match(/height:\s*([^;]+)/)
                if (heightMatch) {
                  actualHeight = heightMatch[1].trim()
                }
              } else if (computedStyle.height && computedStyle.height !== 'auto') {
                actualHeight = computedStyle.height
              }
              
              // Extract numeric value for flex
              const heightNum = parseFloat(actualHeight) || 120
              
              if (style.includes('height') || dataHeight) {
                blankSpace.style.height = actualHeight
                blankSpace.style.minHeight = actualHeight
                blankSpace.style.maxHeight = actualHeight
                blankSpace.style.width = '100%'
                blankSpace.style.display = 'block'
                blankSpace.style.visibility = 'visible'
                blankSpace.style.flex = `0 0 ${actualHeight}`
                blankSpace.style.flexShrink = '0'
                blankSpace.style.flexGrow = '0'
                blankSpace.style.boxSizing = 'border-box'
                blankSpace.style.overflow = 'hidden'
                blankSpace.style.position = 'relative'
                blankSpace.style.backgroundColor = 'transparent'
                blankSpace.style.border = '1px solid transparent'
                blankSpace.style.margin = '0'
                blankSpace.style.padding = '0'
                blankSpace.classList.add('blank-header-space')
                blankSpace.setAttribute('data-header-height', heightNum.toString())
                
                // Ensure spacer span exists with correct height
                const existingSpan = blankSpace.querySelector('span')
                if (existingSpan) {
                  existingSpan.style.height = actualHeight
                  existingSpan.style.lineHeight = actualHeight
                } else {
                  const spacer = printWindow.document.createElement('span')
                  spacer.style.cssText = `display: inline-block; height: ${actualHeight}; width: 1px; opacity: 0; visibility: hidden; position: absolute; font-size: 1px; line-height: ${actualHeight};`
                  spacer.textContent = '\u200B' // Zero-width space
                  blankSpace.appendChild(spacer)
                }
              }
            })
            
            // CRITICAL: Preserve blank footer spaces and ensure footer sticks to bottom
            const blankFooterSpaces = page.querySelectorAll('.blank-footer-space, [data-print-footer="true"] > div[style*="height"], [class*="PmpV2FooterContainer"] > div[style*="height"], [class*="PmpV2FooterContainer"] > div[data-footer-height]')
            blankFooterSpaces.forEach(blankSpace => {
              const style = blankSpace.getAttribute('style') || ''
              const dataHeight = blankSpace.getAttribute('data-footer-height')
              const computedStyle = printWindow.getComputedStyle(blankSpace)
              
              // Get height from data attribute, style attribute, or computed style
              let actualHeight = '120px'
              if (dataHeight) {
                actualHeight = `${dataHeight}px`
              } else if (style.includes('height:')) {
                const heightMatch = style.match(/height:\s*([^;]+)/)
                if (heightMatch) {
                  actualHeight = heightMatch[1].trim()
                }
              } else if (computedStyle.height && computedStyle.height !== 'auto') {
                actualHeight = computedStyle.height
              }
              
              // Extract numeric value for flex
              const heightNum = parseFloat(actualHeight) || 120
              
              if (style.includes('height') || dataHeight) {
                blankSpace.style.height = actualHeight
                blankSpace.style.minHeight = actualHeight
                blankSpace.style.maxHeight = actualHeight
                blankSpace.style.width = '100%'
                blankSpace.style.display = 'block'
                blankSpace.style.visibility = 'visible'
                blankSpace.style.flex = `0 0 ${actualHeight}`
                blankSpace.style.flexShrink = '0'
                blankSpace.style.flexGrow = '0'
                blankSpace.style.boxSizing = 'border-box'
                blankSpace.style.overflow = 'hidden'
                blankSpace.style.position = 'relative'
                blankSpace.style.backgroundColor = 'transparent'
                blankSpace.style.border = '1px solid transparent'
                blankSpace.style.margin = '0'
                blankSpace.style.padding = '0'
                blankSpace.classList.add('blank-footer-space')
                blankSpace.setAttribute('data-footer-height', heightNum.toString())
                
                // Ensure spacer span exists with correct height
                const existingSpan = blankSpace.querySelector('span')
                if (existingSpan) {
                  existingSpan.style.height = actualHeight
                  existingSpan.style.lineHeight = actualHeight
                } else {
                  const spacer = printWindow.document.createElement('span')
                  spacer.style.cssText = `display: inline-block; height: ${actualHeight}; width: 1px; opacity: 0; visibility: hidden; position: absolute; font-size: 1px; line-height: ${actualHeight};`
                  spacer.textContent = '\u200B' // Zero-width space
                  blankSpace.appendChild(spacer)
                }
              }
            })

          })
          // Wait for content to be fully rendered and painted before printing
          // The key is to ensure the browser has fully processed the content
          // so the print dialog doesn't show a loading preview
          const waitForRender = () => {
            return new Promise((resolve) => {
              // Wait for window to fully load and render
              const checkAndPrint = () => {
                try {
                  const doc = printWindow.document
                  const body = doc.body
                  
                  // Ensure document is complete
                  if (doc.readyState !== 'complete') {
                    window.setTimeout(checkAndPrint, 50)
                    return
                  }
                  
                  // Ensure body has content
                  if (!body || body.children.length === 0) {
                    window.setTimeout(checkAndPrint, 50)
                    return
                  }
                  
                  // Check if prescription pages exist and have rendered content
                  const pages = body.querySelectorAll('.prescription-page')
                  if (pages.length === 0) {
                    window.setTimeout(checkAndPrint, 50)
                    return
                  }
                  
                  // Verify pages have actual content (height > 0)
                  let allPagesReady = true
                  for (let i = 0; i < pages.length; i++) {
                    const page = pages[i]
                    const rect = page.getBoundingClientRect()
                    if (rect.height === 0) {
                      allPagesReady = false
                      break
                    }
                  }
                  
                  if (!allPagesReady) {
                    window.setTimeout(checkAndPrint, 50)
                    return
                  }
                  
                  // All checks passed - wait for browser to finish processing
                  // Use multiple paint cycles to ensure everything is rendered
                  let cycles = 0
                  const waitForPaint = () => {
                    printWindow.requestAnimationFrame(() => {
                      cycles++
                      if (cycles >= 3) {
                        // One final delay to let browser finish all processing
                        // This prevents the print dialog from showing loading preview
                        window.setTimeout(() => {
                          console.log(`[PrescriptionMakingPage Quick Print] Content fully ready (${pages.length} pages), triggering print...`)
                          resolve()
                        }, 200) // Small delay to ensure browser is completely ready
                      } else {
                        waitForPaint()
                      }
                    })
                  }
                  
                  waitForPaint()
                } catch (e) {
                  console.warn('[PrescriptionMakingPage Quick Print] Error checking render state:', e)
                  // On error, proceed after a delay
                  window.setTimeout(() => resolve(), 300)
                }
              }
              
              // Start checking after window loads
              printWindow.onload = () => {
                window.setTimeout(checkAndPrint, 100)
              }
              
              // Also check immediately in case onload already fired
              if (printWindow.document.readyState === 'complete') {
                window.setTimeout(checkAndPrint, 100)
              } else {
                printWindow.addEventListener('load', () => {
                  window.setTimeout(checkAndPrint, 100)
                })
              }
              
              // Fallback timeout
              window.setTimeout(() => {
                console.log(`[PrescriptionMakingPage Quick Print] Fallback timeout, proceeding with print...`)
                resolve()
              }, 4000) // 4 second max wait
            })
          }
          
          // Wait for render before printing
          waitForRender().then(() => {
            // Count pages in print window before printing
            let _pagesInWindow = 0
            try {
              if (printWindow && printWindow.document && printWindow.document.body) {
                _pagesInWindow = printWindow.document.body.querySelectorAll('.prescription-page').length
              }
            } catch (e) {
              // Could not count pages in print window
            }
            console.log(`[PrescriptionMakingPage Quick Print] Render ready — signalling popup to print...`)
            // Signal the injected script inside the new tab — it polls this flag and calls
            // window.print() from within its own context (Safari allows this; cross-window call is blocked)
            try { printWindow._rxReadyToPrint = true } catch(_signalErr) {
            }
          })
        })
      }
      
      // Guard: onload may never fire if the popup document already reached readyState 'complete'
      // before we set the handler (cached stylesheets load in < 68ms). Execute setup immediately.
      // Only fire when fully loaded (resources ready). 'interactive' would double-fire
      // once the real onload triggers. Use a one-time guard on the handler itself.
      if (!printWindow?.closed) {
        try {
          if (printWindow.document.readyState === 'complete') {
            printWindow.onload()
          }
        } catch (_guardErr) {
          // cross-origin or window already closed
        }
      }

      console.log(`[PrescriptionMakingPage Quick Print] Opened new window with ${allPages.length} cloned pages`)
    } catch (error) {
      console.error('[PrescriptionMakingPage Quick Print] Error:', error)
      toast.dismiss(loadingToastId)
      toast.error('Failed to prepare pages for printing. Please try again.', { autoClose: 3000 })
    }
  }
  
  async function saveAndPrintDirect(selectedPages = null) {
    // Generate PDF and open print dialog directly (skip drawer)
    // PDF generation will handle saving in background
    try {
      // Show loading toast
      toast.info('Generating PDF for printing...', { autoClose: 2000 })
      
      // Save prescription data in background
      savePrescriptionData(true, true).catch(err => {
        console.warn('Background save failed during print:', err)
      })
      
      // Generate PDF directly without opening drawer
      const pdfBlob = await generatePDFBlobOnly(selectedPages)
      
      // Open PDF in new window and trigger print
      const pdfUrl = window.URL.createObjectURL(pdfBlob)
      const printWindow = window.open(pdfUrl, '_blank')
      
      if (printWindow) {
        // Wait for PDF to load, then trigger print
        const checkLoaded = setInterval(() => {
          try {
            if (printWindow.document && printWindow.document.readyState === 'complete') {
              clearInterval(checkLoaded)
              setTimeout(() => {
                // Inject color mode CSS before printing
                injectColorModeCSS(printWindow, colorMode === 'bw')
                
                printWindow.print()
                // Clean up URL after print dialog opens
                setTimeout(() => {
                  window.URL.revokeObjectURL(pdfUrl)
                }, 1000)
              }, 500)
            }
          } catch (e) {
            // Cross-origin or other error, try print anyway
            clearInterval(checkLoaded)
            setTimeout(() => {
              try {
                printWindow.print()
                setTimeout(() => {
                  window.URL.revokeObjectURL(pdfUrl)
                }, 1000)
              } catch (printErr) {
                console.error('Error triggering print:', printErr)
              }
            }, 500)
          }
        }, 100)
        
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkLoaded)
          try {
            printWindow.print()
            setTimeout(() => {
              window.URL.revokeObjectURL(pdfUrl)
            }, 1000)
          } catch (printErr) {
            console.error('Error triggering print (timeout):', printErr)
          }
        }, 5000)
      } else {
        toast.error('Popup blocked. Please allow popups and try again.', { autoClose: 5000 })
        window.URL.revokeObjectURL(pdfUrl)
      }
    } catch (error) {
      console.error('Error generating PDF for print:', error)
      // Fallback to simple window.print if PDF generation fails
      toast.warning('Falling back to browser print. Some pages may not print correctly.', { autoClose: 3000 })
      window.print()
    }
  }
  
  async function generatePDFBlobOnly(selectedPages = null) {
    // This function generates PDF and returns the blob directly (no drawer)
    // Reuses the same logic as generateClientSidePDFForPrint but skips drawer opening
    
    console.log('[PDF Generation Direct] Starting PDF generation (no drawer)', { selectedPages })
    
    try {
      // Save prescription data asynchronously in background (non-blocking)
      // This ensures data is saved but doesn't block PDF generation
      // Start save in background - don't await it, let it run completely async
      // Suppress toast since this is a background save during PDF generation
      savePrescriptionData(true, true).catch(err => {
        console.warn('Background save failed during print:', err)
      })
      
      // Get A4 dimensions from printSettings
      const currentDpiData = A4DpiToDetailsMap?.[printSettings?.dpi] || A4DpiToDetailsMap[96]
      const heightOfA4Page = currentDpiData?.height || 1123
      const widthOfA4Page = currentDpiData?.width || 794
      
      // Get all prescription pages - avoid duplicates when both main page and Rx drawer are rendered
      let allPages = Array.from(document.querySelectorAll('.prescription-page'))
      
      // Filter out pages from hidden containers
      allPages = allPages.filter(page => {
        const pageStyle = window.getComputedStyle(page)
        if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
          return false
        }
        
        let parent = page.parentElement
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent)
          if (style.display === 'none' || style.visibility === 'hidden') {
            return false
          }
          if (parent.classList.contains('MuiDrawer-root') && style.display === 'none') {
            return false
          }
          parent = parent.parentElement
        }
        return true
      })
      
      // Deduplicate by page number
      const pagesByNumber = new Map()
      allPages.forEach(page => {
        const pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page')
        if (pageNum) {
          if (!pagesByNumber.has(pageNum)) {
            pagesByNumber.set(pageNum, page)
          } else {
            const existingPage = pagesByNumber.get(pageNum)
            const isExistingInDrawer = existingPage.closest('.PMPPrintWrapper') !== null
            const isCurrentInDrawer = page.closest('.PMPPrintWrapper') !== null
            
            if (isExistingInDrawer && !isCurrentInDrawer) {
              pagesByNumber.set(pageNum, page)
            }
          }
        } else {
          pagesByNumber.set(`no-number-${pagesByNumber.size}`, page)
        }
      })
      
      allPages = Array.from(pagesByNumber.values())
      allPages.sort((a, b) => {
        const pageNumA = parseInt(a.getAttribute('data-page-number') || a.getAttribute('data-pdf-page')) || 0
        const pageNumB = parseInt(b.getAttribute('data-page-number') || b.getAttribute('data-pdf-page')) || 0
        return pageNumA - pageNumB
      })
      
      // Filter pages if specific pages are selected
      if (selectedPages && selectedPages.length > 0) {
        allPages = allPages.filter((page, idx) => {
          const pageNum = parseInt(page.getAttribute('data-page-number')) || (idx + 1)
          return selectedPages.includes(pageNum)
        })
      }
      
      if (!allPages || allPages.length === 0) {
        throw new Error('No prescription pages found to print')
      }
      
      console.log(`[PDF Generation Direct] Found ${allPages.length} page(s) to process`)
      
      // Create a container for all pages
      const pdfContainer = document.createElement('div')
      pdfContainer.style.width = `${widthOfA4Page}px`
      pdfContainer.style.margin = '0'
      pdfContainer.style.padding = '0'
      pdfContainer.style.backgroundColor = '#fff'
      pdfContainer.style.display = 'block'
      pdfContainer.style.position = 'relative'
      
      const _createdElements = []
      
      // Clone pages (same logic as generateClientSidePDFForPrint)
      for (let idx = 0; idx < allPages.length; idx++) {
        const page = allPages[idx]
        const clonedPage = page.cloneNode(true)
        
        const hideElements = clonedPage.querySelectorAll('.hide-on-print-only, .hide-on-print')
        hideElements.forEach(el => el.remove())
        
        // Style the cloned page
        clonedPage.style.width = 'auto'
        clonedPage.style.minWidth = `${widthOfA4Page}px`
        clonedPage.style.maxWidth = 'none'
        clonedPage.style.height = 'auto'
        clonedPage.style.minHeight = `${heightOfA4Page}px`
        clonedPage.style.maxHeight = 'none'
        clonedPage.style.margin = '0'
        clonedPage.style.padding = '0'
        clonedPage.style.display = 'block'
        clonedPage.style.position = 'relative'
        clonedPage.style.boxSizing = 'border-box'
        clonedPage.style.overflow = 'visible'
        
        clonedPage.setAttribute('data-pdf-page', idx + 1)
        pdfContainer.appendChild(clonedPage)
      }
      
      // Create temporary container
      const tempContainer = document.createElement('div')
      tempContainer.className = 'temp-pdf-container'
      tempContainer.style.position = 'absolute'
      tempContainer.style.left = '-9999px'
      tempContainer.style.top = '0'
      tempContainer.style.width = `${widthOfA4Page}px`
      tempContainer.style.backgroundColor = '#fff'
      tempContainer.appendChild(pdfContainer)
      document.body.appendChild(tempContainer)
      
      // Generate PDF
      const { jsPDF } = await import('jspdf')
      const pdf = new jsPDF({
        unit: 'px',
        format: 'a4',
        orientation: 'portrait',
        compress: true,
      })
      
      const pdfPageWidth = pdf.internal.pageSize.getWidth()
      const pdfPageHeight = pdf.internal.pageSize.getHeight()
      
      let pdfPageCount = 0
      for (let idx = 0; idx < allPages.length; idx++) {
        const pageElement = pdfContainer.children[idx]
        if (!pageElement) continue
        
        const hasTextContent = pageElement.textContent.trim().length > 0
        const hasImages = pageElement.querySelectorAll('img, canvas, svg').length > 0
        const hasTables = pageElement.querySelectorAll('table').length > 0
        const hasContent = hasTextContent || hasImages || hasTables
        
        if (!hasContent) continue
        
        if (pdfPageCount > 0) {
          pdf.addPage()
        }
        pdfPageCount++
        
        const pageRect = pageElement.getBoundingClientRect()
        const scrollWidth = pageElement.scrollWidth || pageRect.width
        const scrollHeight = pageElement.scrollHeight || pageRect.height
        
        const captureWidth = Math.max(scrollWidth, widthOfA4Page)
        const captureHeight = Math.min(scrollHeight, pdfPageHeight)
        
        const originalWidth = pageElement.style.width
        const originalMaxWidth = pageElement.style.maxWidth
        pageElement.style.width = `${captureWidth}px`
        pageElement.style.maxWidth = 'none'
        
        try {
          const html2canvas = (await import('html2canvas')).default
          const canvasScale = printSettings?.canvasScale || 1.0
          
          const canvas = await html2canvas(pageElement, {
            scale: canvasScale,
            useCORS: false,
            logging: false,
            letterRendering: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            width: captureWidth,
            height: captureHeight,
            windowWidth: captureWidth,
            windowHeight: captureHeight,
            x: 0,
            y: 0,
            scrollX: 0,
            scrollY: 0,
            removeContainer: false,
          })
          
          const canvasActualWidth = canvas.width / canvasScale
          const canvasActualHeight = canvas.height / canvasScale
          
          const scaleX = pdfPageWidth / canvasActualWidth
          const scaleY = pdfPageHeight / canvasActualHeight
          const scale = Math.min(scaleX, scaleY)
          
          const finalWidth = canvasActualWidth * scale
          const finalHeight = canvasActualHeight * scale
          
          const imgData = canvas.toDataURL('image/jpeg', 0.85)
          pdf.addImage(imgData, 'JPEG', 0, 0, finalWidth, finalHeight, undefined, 'FAST')
          
          // Clean up canvas
          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
          }
          canvas.width = 0
          canvas.height = 0
          
          pageElement.style.width = originalWidth
          pageElement.style.maxWidth = originalMaxWidth
        } catch (error) {
          pageElement.style.width = originalWidth
          pageElement.style.maxWidth = originalMaxWidth
          console.error(`Error processing page ${idx + 1}:`, error)
          continue
        }
      }
      
      // Cleanup
      try {
        if (tempContainer && tempContainer.parentNode) {
          while (tempContainer.firstChild) {
            tempContainer.removeChild(tempContainer.firstChild)
          }
          document.body.removeChild(tempContainer)
        }
      } catch (cleanupError) {
        console.warn('Error cleaning up temp container:', cleanupError)
      }
      
      // Return PDF blob
      const pdfBlob = pdf.output('blob')
      console.log('[PDF Generation Direct] PDF blob created, size:', pdfBlob.size, 'bytes')
      return pdfBlob
      
    } catch (error) {
      console.error('[PDF Generation Direct] Error generating PDF:', error)
      throw error
    }
  }
  
  async function saveAndDownloadPDF({ sendImmediately = false }) {
    try {
      // Always use server-side method for downloads (fetch PDF from server)
      await savePrescriptionData()
      await sleep(800)
      const data = await generatePrescriptionPDF({
        bookedEventId: bookedEventInfo?.id,
        sendImmediately,
      })
      if (!!data && data?.pdfMediaId) {
        const pdfUrl = `${mediaURL}${data?.pdfMediaId}`
        const filename = `prescription_${
          prescriptionData?.patientPrefill?.patientNumber
        }_${dayjs().format('YYYY-MM-DD HH:mm:ss')}.pdf`
        
        // Add a small delay before attempting download to allow CloudFront to propagate
        await sleep(1000)
        
        // Fetch PDF and show in drawer instead of directly downloading
        setIsDownloadMode(true)
        setPdfGenerating(true)
        setPdfProgressMessage('Fetching PDF from server...')
        setPdfDrawerOpen(true)
        
        const result = await downloadPDFFromURL(pdfUrl, filename, 3, 2000, true)
        
        setPdfGenerating(false)
        
        if (result.success && result.blob) {
          // Create blob URL for drawer display
          const blobUrl = URL.createObjectURL(result.blob)
          setPdfUrl(blobUrl)
          setPdfBlob(result.blob)
          setPdfProgressMessage('')
          toast.success('PDF loaded successfully!', { autoClose: 2000 })
          
          // Auto-download the file when showing in drawer
          try {
            const downloadUrl = URL.createObjectURL(result.blob)
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = filename
            document.body.appendChild(link)
            link.click()
            setTimeout(() => {
              URL.revokeObjectURL(downloadUrl)
              document.body.removeChild(link)
            }, 100)
          } catch (downloadError) {
            console.warn('Auto-download failed:', downloadError)
            // Non-critical error, continue
          }
        } else {
          // Error already shown in downloadPDFFromURL
          console.error('Failed to fetch PDF after retries')
          setPdfProgressMessage('Failed to load PDF. Please try again.')
          // Keep drawer open to show error, user can close it
        }
      } else {
        toast.error('PDF generation failed. Please try again.', { autoClose: 5000 })
      }
    } catch (error) {
      console.error('Error in saveAndDownloadPDF:', error)
      setPdfGenerating(false)
      setPdfProgressMessage('Error: ' + (error.message || 'Failed to generate or download PDF'))
      toast.error('Failed to generate or download PDF. Please try again.', {
        autoClose: 5000,
      })
    }
  }
  
  function handleDownloadPDFFromDrawer() {
    if (!pdfBlob) {
      toast.error('PDF not available for download', { autoClose: 3000 })
      return
    }
    
    const filename = `prescription_${
      prescriptionData?.patientPrefill?.patientNumber || 'Patient'
    }_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.pdf`
    
    // Create download link
    const downloadUrl = URL.createObjectURL(pdfBlob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    
    // Trigger download
    document.body.appendChild(link)
    link.click()
    
    // Cleanup
    setTimeout(() => {
      URL.revokeObjectURL(downloadUrl)
      document.body.removeChild(link)
    }, 100)
    
    toast.success('PDF downloaded successfully!', { autoClose: 2000 })
  }
  
  async function handleSendPDFToPatient() {
    if (!bookedEventInfo?.id) {
      toast.error('Event information not available', { autoClose: 3000 })
      return
    }
  
    try {
      setPdfGenerating(true)
      setPdfProgressMessage('Sending PDF to patient...')
      
      // Save prescription first
      await savePrescriptionData()
      await sleep(800)
      
      // Generate and send PDF to patient
      const data = await generatePrescriptionPDF({
        bookedEventId: bookedEventInfo?.id,
        sendImmediately: true, // This sends the PDF via email to patient
      })
      
      setPdfGenerating(false)
      setPdfProgressMessage('')
      
      if (data?.pdfMediaId) {
        toast.success('PDF sent to patient successfully!', { autoClose: 3000 })
      } else {
        toast.error('Failed to send PDF to patient. Please try again.', { autoClose: 5000 })
      }
    } catch (error) {
      console.error('Error sending PDF to patient:', error)
      setPdfGenerating(false)
      setPdfProgressMessage('')
      toast.error('Failed to send PDF to patient. Please try again.', {
        autoClose: 5000,
      })
    }
  }
  
  
  return {
    saveAndPrint,
    quickPrint,
    saveAndPrintDirect,
    generatePDFBlobOnly,
    saveAndDownloadPDF,
    handleDownloadPDFFromDrawer,
    handleSendPDFToPatient,
  }
}
